import { addressLine } from '../address-words'
import { currencySymbol, currencyWord, cyclesOn, monthlyCycles, roundConfig, postcodeWords } from '../round-config'
import { api, mutateUpload, postNow, ApiError } from '../api'
import {
  buildCustomers,
  FIELD_LABELS,
  headerChoice,
  bodyRows,
  priceTie,
  regionQuestion,
  nameOrderQuestion,
  guessedColumns,
  guessFields,
  parseCsv,
  sameCustomerKey,
  sheetScore,
  squeegeeJobs,
  isSqueegeeJobs,
  tidyRows,
  type ImportField,
  type ImportRow, isHistoryHeading, type YearMode, numericDateCells, numericDateOrder, parseVisitDate, type DateOrder,
  howLongAgo, missingDetails, type MissingDetail, sheetLooksOld } from '../import-sheet'
import { getMe, loadMe } from '../me'
import { countBetween } from '../motion'
import { routeGen } from '../route-state'
import type { Customer } from '../types'
import { clear, cycleLabel, cycleOptions, cycleValueFor, dayLabel, el, money, parseMoney, shortDate, toast, todayISO } from '../ui'
import { alreadyUndone, count, moneyStaysWords, namesWords, undoResultWords, whatWords, type MoneyChange, type UndoResult } from '../import-undo-result'
import { SEND_MAX_BYTES, SEND_TYPES, sentCard, sizeWords, waitingFiles, type ImportRequest } from '../import-requests'
import { goToNoDate, markNoDateForGlow, summariseRound, type RoundCustomer } from '../attention'
import { renderWelcome } from '../welcome'
import { answerAll, historyChunks, KIND_WORDS, moneyOnly, partsOf, moneyRefusal, moneyToCheck, optionWords, placeMoney, resumeHistory, settled as settledMoney, totalsWords, type Brought, type Existing, type KnownAccount, type MoneyState, type Person as MoneyPerson, type Placed } from '../import-money'
import { pounds, type MoneyQuestion } from '../squeegee-money'

// Import customers (#/import, owners; Liam 2026-09-26: "Start with sign up
// page and import", onboarding "super clear and easy"). Three steps on one
// screen:
//   1. choose the file (Excel, Numbers, CSV, or another app's export);
//   2. check what Roundly read: every customer on one line, ticked, the
//      incomplete ones first, each one changeable with a tap; then each column
//      with an example and what it goes into (changeable);
//   3. add them, 100 at a time, then straight on to the next thing to do.
// Customers already on the round (same name and postcode) are left out, so
// importing the same file twice adds nobody twice.

const MAX_BYTES = 8 * 1024 * 1024
// A Squeegee backup is packed small: this is a very big round's worth.
const MAX_BACKUP_BYTES = 32 * 1024 * 1024
const MAX_CUSTOMERS = 5000
const CHUNK = 100
// Well under the server's request size limit, however long the notes are.
const CHUNK_BYTES = 200_000

type ImportSource = 'squeegee' | 'spreadsheet' | 'app' | 'unsure'
let importSource: ImportSource = 'unsure'
// Squeegee is a UK app: a round anywhere else is not offered it by name (Step 2 round 6).
const squeegeeOffered = (): boolean => roundConfig().country === 'GB'
let journey: HTMLElement | null = null
function importStage(stage: 'choose' | 'check' | 'saving' | 'result'): void {
  if (!journey?.isConnected) return
  const active = stage === 'choose' ? 0 : stage === 'check' ? 1 : 2
  journey.replaceChildren(...['Choose file', 'Check customers', stage === 'result' ? 'Result' : 'Add to Roundly'].map((label, i) =>
    el('li', { class: i < active ? 'is-complete' : i === active ? 'is-active' : '', ...(i === active ? { 'aria-current': 'step' } : {}) },
      el('span', { class: 'import-stage-number', 'aria-hidden': 'true' }, i < active ? '✓' : String(i + 1)), el('span', {}, label))))
}

function fileStatus(box: HTMLElement, words: string): void {
  clear(box)
  box.append(el('section', { class: 'import-card import-reading', role: 'status', 'aria-live': 'polite' },
    el('h3', {}, words), el('p', { class: 'muted' }, 'Next, check what Roundly found. Nothing is added until you choose Add.')))
}

interface Sheet {
  file: string
  header: string[] | null
  body: string[][]
  // Each body row's spreadsheet row number.
  lines: number[]
  // Rows above the column names with 2+ cells (a note, or a customer typed
  // above them), and those the owner ticked to bring in as customers.
  above: number[]
  // Squeegee's export lists each job on its own row: the customers whose jobs came in as one.
  joined: { line: number; name: string; jobs: number }[]
  keepAbove: Set<number>
  // What Roundly first made of the sheet, kept for the import feedback: its
  // column choices, the headings row it picked, whether it asked about it, and
  // how many columns it asked to confirm.
  firstFields: ImportField[]
  headerGuessed: number
  headerUnsureAtStart: boolean
  guessedAtStart: number
  firstLine: number
  fields: ImportField[]
  // A workbook's other sheets, by name, so the owner can switch if Roundly
  // picked the wrong one. From a Squeegee backup, one sheet, with the customers who are not coming in.
  sheets: SheetData[]
  sheetName: string | null
  sheetIndex: number
  // Which row holds the column names (-1: none), and whether Roundly was
  // unsure and the owner has not yet said (then the screen asks first).
  headerRow: number
  headerUnsure: boolean
}

// money: a Squeegee backup's money owed and credit (0043, release step 6), only on a round with brought money on.
interface SheetData { name: string; rows: string[][]; notIn?: { name: string; reason: string }[]; money?: MoneyState }

let sheet: Sheet | null = null
// Who is already on the round (name, postcode and house number), read once
// per visit to this screen. null means not read yet; a failed read is not
// kept, so the next file tries again.
let existingKeys: Set<string> | null = null
// The same customers by key, for adding last visits to anyone already here.
let existingByKey: Map<string, Customer> = new Map()
let dupCheckFailed = false
// The owner's two answers about past visits (28 Sep 2026): which year dates
// with no year are from (only asked when the sheet does not say), and whether
// the visits were paid. Asked once per file, never guessed.
let yearMode: YearMode = 'recent'
// Which way round "03/04" is read, when the owner has said (a US or Canadian round only; Step 2 round 2).
let dateOrderChosen: DateOrder | null = null
let yearChosen = false
let visitsPaid: boolean | null = null
// The owner's choices about the customers themselves (Liam 28 Sep 2026: "an
// easy way to approve customers at a glance"), by spreadsheet row number: who
// is ticked or not, what they changed on a customer, whose details are
// open for changing, and whether each list is shown in full.
const EDIT_KEYS = ['name', 'address', 'postcode', 'phone', 'reg_price_pence', 'cycle_days', 'cycle_text'] as const
type EditKey = (typeof EDIT_KEYS)[number]
// A changed value, or null for one the owner cleared.
type RowEdit = { [K in EditKey]?: ImportRow[K] | null }
// true = ticked, false = unticked; a customer not in it has never been
// touched (ticked, unless it is a second copy of an earlier row).
let picked = new Map<number, boolean>()
let edits = new Map<number, RowEdit>()
let openLine: number | null = null
let lastChanged: number | null = null
let showAll = { incomplete: false, ready: false, duplicate: false }
// "Is this sheet up to date?", asked only when its newest visit is weeks old:
// 'out' leaves the dates out (their columns go to Don't import, and come back
// with "Put the dates back"), 'keep' keeps them.
let sheetAnswer: 'keep' | 'out' | null = null
let fieldsBeforeOut: ImportField[] | null = null
let newestWhenOut: string | null = null
// The owner's answer to "Are these areas or customers?" (one-cell rows).
let sectionsOff = false
let sectionsAnswer: 'areas' | 'customers' | null = null
// The owner said which column is the regular price (the question, or the box).
let priceAnswered = false
// The owner said whether a repeating "Region" is part of the address or the area.
let regionAnswered = false
// The owner confirmed the columns Roundly worked out from their contents, for
// these exact column choices: any change afterwards asks again (Astra audit).
let guessesConfirmedFor: string | null = null
// "Check what goes where" opened by the owner (it starts folded; see drawCheck).
let colsOpen = false

function resetChoices(): void {
  yearMode = 'recent'
  dateOrderChosen = null
  // "The last 12 months" is shown picked for dates with no year (Liam, 29 Sep). It only counts as answered when no
  // such date lands in an earlier year; if one does ("16 Oct" in September is last October), the owner confirms
  // it (Codex, 29 Sep).
  yearChosen = false
  visitsPaid = null
  picked = new Map()
  edits = new Map()
  openLine = null
  lastChanged = null
  showAll = { incomplete: false, ready: false, duplicate: false }
  sheetAnswer = null
  fieldsBeforeOut = null
  newestWhenOut = null
  sectionsOff = false
  sectionsAnswer = null
  priceAnswered = false
  regionAnswered = false
  guessesConfirmedFor = null
  colsOpen = false
}

function applyEdit(r: ImportRow, e: RowEdit | undefined): ImportRow {
  if (!e) return r
  const out: Partial<ImportRow> = { ...r }
  for (const k of EDIT_KEYS) {
    if (!(k in e)) continue
    const v = e[k]
    if (v === null || v === undefined) delete out[k]
    else (out as Record<string, unknown>)[k] = v
  }
  return out as ImportRow
}

export function mountImport(): void {
  sheet = null
  importSource = 'unsure'
  journey = null
  existingKeys = null
  resetChoices()
  const root = document.querySelector<HTMLElement>('#screen')!
  clear(root)
  if (getMe()?.role !== 'owner') {
    root.append(el('p', { class: 'empty-note' }, 'Only the owner of a round can import customers.'))
    return
  }
  root.append(
    el(
      'div',
      { class: 'page-heading' },
      // Liam's offer line (1 Oct 2026).
      el('div', {}, el('h2', {}, 'Import your customers'), el('p', { class: 'muted' }, "Choose a file, check your customers, then bring your round over.")),
    ),
  )
  journey = el('ol', { class: 'import-journey', 'aria-label': 'Import progress' })
  root.append(journey)
  importStage('choose')
  // Above the steps: "Undo this import" for an import in the last 7 days, and "We've got your file"
  // for a file sent and still waiting (0039). Drawn when they answer; nothing if a read fails.
  const top = el('div', { class: 'import-body import-top' })
  const body = el('div', { class: 'import-body' })
  root.append(top, body)
  showChoose(body)
  void drawTop(top)
  // From the start screen's "Send it to us" (#/import?send=1): straight to the send card.
  if (/[?&]send=1\b/.test(window.location.hash)) {
    requestAnimationFrame(() => body.querySelector<HTMLElement>('.import-send')?.scrollIntoView({ block: 'start' }))
  }
}

async function drawTop(top: HTMLElement): Promise<void> {
  const gen = routeGen
  const [page, waiting] = await Promise.all([undoableBatches(), waitingFiles()])
  if (gen !== routeGen || !top.isConnected) return
  // Every import still inside its 7 days, newest first, each with its own Undo (or why it can't be),
  // five at a time (Astra, 1 Oct: one that can't be undone must not hide an older one that can).
  const imports = el('div', { class: 'import-body' })
  const addPage = (p: BatchPage): void => {
    imports.append(...p.batches.map((b) => undoCard(b)))
    if (!p.more || !p.batches.length) return
    const older = el('button', { type: 'button', class: 'btn-secondary import-older' }, 'Show older imports')
    older.addEventListener('click', () => {
      older.disabled = true
      void undoableBatches(p.batches[p.batches.length - 1].id).then((next) => {
        older.remove()
        addPage(next)
      })
    })
    imports.append(older)
  }
  addPage(page)
  top.replaceChildren(...(page.batches.length ? [imports] : []), ...waiting.map((r) => sentCard(r)))
}

function showChoose(box: HTMLElement, problem?: string): void {
  importStage('choose')
  clear(box)
  const input = el('input', { type: 'file', accept: '.csv,.tsv,.txt,.xlsx,.xls,.xlsm,.ods,.numbers,.sqg,text/csv,application/x-7z-compressed', class: 'import-file' }) as HTMLInputElement
  const choose = el('button', { type: 'button', class: 'btn-primary import-choose' }, 'Choose your file')
  choose.addEventListener('click', () => input.click())
  input.addEventListener('change', () => {
    const f = input.files?.[0]
    if (f) void readFile(box, f)
  })
  const card = el('section', { class: 'import-card import-source-card' })
  const sources = el('div', { class: 'import-sources', role: 'group', 'aria-label': 'Where your customers are now' })
  const help = el('div', { class: 'import-source-help' })
  const buttons: { key: ImportSource; button: HTMLButtonElement }[] = []
  const squeegee = squeegeeOffered()
  const describe = (): void => {
    for (const { key, button } of buttons) button.setAttribute('aria-pressed', String(key === importSource))
    help.replaceChildren()
    if (importSource === 'squeegee') {
      help.append(el('h4', {}, 'Download your Squeegee backup'),
        el('p', { class: 'muted' }, 'On a computer, open Squeegee. Your backup includes gate codes, notes and the next visits it has arranged.'),
        el('ol', { class: 'import-steps' },
          el('li', {}, 'Open Settings (top left), then Data, then Manage Backups.'),
          el('li', {}, 'Click ', el('strong', {}, 'Run a manual backup now'), '.'),
          el('li', {}, 'Click the three dots beside the newest backup, then ', el('strong', {}, 'Download'), '.'),
          el('li', {}, 'Choose that file here. Its name ends in .sqg.')),
        el('details', { class: 'import-alternative' }, el('summary', {}, 'Using a CSV export instead?'),
          el('p', {}, 'In Squeegee: Insights, Reporting, All job data, then the three dots and Download. Choose All_Jobs.csv here. A backup carries more detail, including gate codes.')))
    } else if (importSource === 'spreadsheet') {
      help.append(el('h4', {}, 'Use your spreadsheet as it is'),
        el('p', {}, 'Excel, Numbers and CSV files all work. You can check and change the details before adding anyone.'),
        el('p', { class: 'muted' }, 'Google Sheets: File, Download, Comma-separated values (.csv).'))
    } else if (importSource === 'app') {
      help.append(el('h4', {}, 'Export your customer list'),
        el('p', {}, 'In your old app, look for Export or Download. Choose a customer list as CSV or Excel, then choose that file here.'),
        el('p', { class: 'muted' }, 'Keep the original file. Roundly will show what it can bring across before anything is added.'))
    } else {
      help.append(el('h4', {}, 'Choose the file you have'),
        el('p', {}, squeegee ? 'A spreadsheet or customer export is a good place to start. Excel, Numbers, CSV and Squeegee backups all work.'
          : 'A spreadsheet or customer export is a good place to start. Excel, Numbers and CSV files all work.'),
        el('p', { class: 'muted' }, 'No file? You can ', el('a', { href: '#/grid?add=1' }, 'add customers one at a time'), ' or use the help below.'))
    }
  }
  for (const [key, label, detail] of [
    ['squeegee', 'Squeegee', 'Use your latest backup'], ['spreadsheet', 'A spreadsheet', 'Excel, Numbers or Google Sheets'],
    ['app', 'Another app', 'Use a customer export'], ['unsure', 'Not sure yet', 'Start with the file you have'],
  ] as const) {
    if (key === 'squeegee' && !squeegee) continue
    const button = el('button', { type: 'button', class: 'import-source', 'aria-pressed': 'false' }, el('strong', {}, label), el('span', {}, detail), el('span', { class: 'import-source-check', 'aria-hidden': 'true' }, '✓'))
    button.addEventListener('click', () => { importSource = key; describe() })
    buttons.push({ key, button }); sources.append(button)
  }
  describe()
  card.append(
    el('h3', {}, 'Where are your customers now?'),
    el('p', { class: 'muted' }, 'Choose one for the right file instructions. You can use any supported file.'), sources, help,
  )
  if (problem) card.append(el('p', { class: 'form-error', role: 'alert' }, problem))
  card.append(el('div', { class: 'import-file-action' }, choose, el('p', { class: 'muted' }, 'Choose → check → add. Your old app stays as it is.')), input)
  box.append(card, sendCard())
}

// ---- "Rather we did it? Send us your file" (0039; Liam 1 Oct 2026) --------------------------------
// The owner picks the file their old app or spreadsheet gave them, says which app if they like, and
// it goes privately to Roundly: Liam puts the customers in within 2 working days. The file is sent
// with one Idempotency-Key per chosen file, kept across retries, so a send repeated after lost
// signal is kept once.

function sendCard(): HTMLElement {
  const card = el('section', { class: 'import-card import-send', id: 'send-file' })
  const input = el('input', { type: 'file', accept: SEND_TYPES.map((t) => `.${t}`).join(','), class: 'import-file' }) as HTMLInputElement
  const choose = el('button', { type: 'button', class: 'btn-secondary import-choose' }, 'Choose the file to send')
  choose.addEventListener('click', () => input.click())
  const problem = el('p', { class: 'form-error', role: 'alert' })
  const intro = (): void => {
    card.replaceChildren(
      el('h3', {}, 'Rather we did it? Send us your file'),
      el('p', {}, "Send the file your old app or spreadsheet gives you, as it is. We'll put your customers in for you, free, within 2 working days."),
      el('p', { class: 'muted' }, `CSV, Excel, Numbers, ODS${squeegeeOffered() ? ', PDF or a Squeegee backup' : ' or PDF'}, up to 10 MB. Only Roundly sees it. A photo of a notebook? Email it to hello@roundly.work.`),
      problem,
      choose,
      input,
    )
  }
  input.addEventListener('change', () => {
    const f = input.files?.[0]
    input.value = ''
    if (!f) return
    const ext = (f.name.toLowerCase().match(/\.([a-z]+)$/) ?? [])[1] ?? ''
    if (!(SEND_TYPES as readonly string[]).includes(ext)) {
      problem.textContent = `Send a CSV, Excel (xlsx or xls), Numbers, ODS or PDF file${squeegeeOffered() ? ', or a Squeegee backup' : ''}.`
      return
    }
    if (f.size > SEND_MAX_BYTES) {
      problem.textContent = 'That file is over 10 MB. Save just the customers and send that.'
      return
    }
    if (f.size === 0) {
      problem.textContent = 'That file is empty.'
      return
    }
    problem.textContent = ''
    ready(f)
  })
  const ready = (f: File): void => {
    const key = crypto.randomUUID()
    const note = el('input', { type: 'text', maxlength: '500', placeholder: 'For example Squeegee, Cleaner Planner or my own spreadsheet', autocomplete: 'off' }) as HTMLInputElement
    const go = el('button', { type: 'button', class: 'btn-primary import-go' }, 'Send it to us')
    const other = el('button', { type: 'button', class: 'btn-secondary' }, 'Choose a different file')
    other.addEventListener('click', () => {
      intro()
      input.click()
    })
    const err = el('p', { class: 'form-error', role: 'alert' })
    go.addEventListener('click', () => {
      go.disabled = true
      other.disabled = true
      go.textContent = 'Sending…'
      err.textContent = ''
      void mutateUpload<ImportRequest>({ path: '/api/import-requests', key, fields: note.value.trim() ? { note: note.value.trim() } : {}, file: f, filename: f.name, fileField: 'file', ackField: 'request' }).then((ack) => {
        if (!card.isConnected) return
        if (ack.ok && ack.row) {
          card.replaceWith(sentCard(ack.row))
          toast("File sent. We'll put your customers in.")
          return
        }
        go.disabled = false
        other.disabled = false
        go.textContent = 'Send it to us'
        // The same key goes again on the next tap: a file that did arrive is kept once.
        err.textContent = ack.status === 0 ? "Your file didn't send. Check your signal and tap Send again." : ack.error ?? "Your file didn't send. Try again."
      })
    })
    card.replaceChildren(
      el('h3', {}, 'Rather we did it? Send us your file'),
      el('p', {}, el('strong', {}, f.name), ` (${sizeWords(f.size)})`),
      el('label', { class: 'import-edit-field' }, el('span', {}, 'Which app did it come from? (if you like)'), note),
      err,
      el('div', { class: 'import-actions' }, go, other),
    )
  }
  intro()
  return card
}

// ---- "Undo this import" (0039; Liam 1 Oct 2026) ----------------------------------------------------
// For 7 days: removes exactly the customers and visits that import brought in, unless they have been
// changed since (those are kept and named). The owner sees what will go before anything does.

interface UndoableBatch {
  id: number
  imported_on: string
  undo_until: string
  customer_count: number
  visit_count: number
  // Money brought from Squeegee (0043): the accounts that go, owed and credit apart; only when it brought any.
  remove: { customers: number; visits: number; accounts?: number; owed_pence?: number; credit_pence?: number }
  kept: string[]
  can_undo?: boolean
  // Money this import changed for customers already here: it stays (moneyStaysWords).
  money_changes?: MoneyChange[]
}
interface BatchPage { batches: UndoableBatch[]; more: boolean }

// One page of imports still inside their 7 days, newest first; `before` for the next, older page.
async function undoableBatches(before?: number): Promise<BatchPage> {
  try {
    const r = await api<{ batches: UndoableBatch[]; more?: boolean }>(`/api/import-batches${before ? `?before=${before}` : ''}`)
    return { batches: r.batches, more: !!r.more }
  } catch {
    return { batches: [], more: false }
  }
}

function undoCard(b: UndoableBatch, inDone = false): HTMLElement {
  const card = el('section', { class: inDone ? 'import-undo import-undo-inline' : 'import-card import-undo' })
  const on = dayLabel(b.imported_on)
  const total = b.remove.customers + b.remove.visits + (b.remove.accounts ?? 0)
  // "£612.50 owed and £45 credit from Squeegee", apart, never netted.
  const moneyGoes = [b.remove.owed_pence ? `${pounds(b.remove.owed_pence)} owed` : '', b.remove.credit_pence ? `${pounds(b.remove.credit_pence)} credit` : ''].filter(Boolean).join(' and ')
  const start = (): void => {
    const open = el('button', { type: 'button', class: 'btn-secondary' }, 'Undo this import')
    open.addEventListener('click', confirm)
    const lines: HTMLElement[] = inDone
      ? [el('p', { class: 'muted' }, `Not what you expected? You can undo this import until ${dayLabel(b.undo_until)}.`)]
      : [
          el('h3', {}, `Your import on ${on}`),
          // The undo date only when there is something left to undo (bug hunt 8 Oct 2026).
          el('p', {}, `It brought in ${whatWords(b.customer_count, b.visit_count) || 'nothing'}${moneyGoes ? `, with ${moneyGoes} from Squeegee` : ''}.${total ? ` You can undo it until ${dayLabel(b.undo_until)}.` : ''}`),
        ]
    card.replaceChildren(
      ...lines,
      ...(total
        ? [open]
        : [el('p', { class: 'muted' }, `Can't undo this one: you've changed everything it brought in${b.kept.length ? ` (${namesWords(b.kept)})` : ''}.`)]),
    )
  }
  const confirm = (): void => {
    const yes = el('button', { type: 'button', class: 'btn-danger' }, 'Remove them')
    const no = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
    const err = el('p', { class: 'form-error', role: 'alert' })
    no.addEventListener('click', start)
    yes.addEventListener('click', () => {
      yes.disabled = true
      no.disabled = true
      yes.textContent = 'Removing…'
      void runUndo(b.id).then((r) => {
        if (!card.isConnected) return
        // Customers and visits have gone: everything showing them reads again.
        existingKeys = null
        window.dispatchEvent(new CustomEvent('dropby:data-changed'))
        const words = undoResultWords(r)
        card.replaceChildren(
          el('h3', {}, words.heading),
          ...words.lines.map((line, i) => el('p', i === words.lines.length - 1 ? { class: 'muted' } : {}, line)),
        )
        toast(words.toast)
      }, (e: unknown) => {
        if (!card.isConnected) return
        yes.disabled = false
        no.disabled = false
        yes.textContent = 'Remove them'
        err.textContent = e instanceof ApiError && e.status >= 400 && e.status < 500 ? e.message : "That didn't finish. Check your signal and tap Remove them again."
      })
    })
    card.replaceChildren(
      el('h3', {}, `Remove ${whatWords(b.remove.customers, b.remove.visits) || 'the money'} brought in on ${on}?`),
      ...(moneyGoes ? [el('p', {}, `With them: ${moneyGoes} from Squeegee.`)] : []),
      ...(moneyStaysWords(b.money_changes) ? [el('p', {}, moneyStaysWords(b.money_changes))] : []),
      ...(b.kept.length ? [el('p', {}, `${count(b.kept.length, 'customer', 'customers')} you've changed since will stay: ${namesWords(b.kept)}.`)] : []),
      el('p', { class: 'muted' }, 'Everything else on your round stays as it is.'),
      err,
      el('div', { class: 'import-actions' }, yes, no),
    )
  }
  start()
  return card
}

// Undo in as many parts as the server asks for (a big import goes a few hundred at a time).
// An undo that had already finished (its last answer was lost, and this is the owner's next tap) comes
// back as finished, with the server's counts for the whole import.
async function runUndo(id: number): Promise<UndoResult> {
  let customers = 0
  let visits = 0
  let kept: string[] = []
  let moneyChanges: MoneyChange[] | undefined
  for (let i = 0; i < 50; i++) {
    let r: { removed: { customers: number; visits: number }; kept: string[]; done: boolean; money_changes?: MoneyChange[] }
    try {
      r = await postNow(`/api/import-batches/${id}/undo`, {})
    } catch (e) {
      const finished = e instanceof ApiError ? alreadyUndone(e.body) : null
      if (!finished) throw e
      // The server's counts cover the whole import, any parts this run removed included.
      return finished
    }
    customers += r.removed.customers
    visits += r.removed.visits
    kept = r.kept
    if (r.money_changes) moneyChanges = r.money_changes
    if (r.done) break
  }
  return { customers, visits, kept, already: false, ...(moneyChanges ? { moneyChanges } : {}) }
}

async function readFile(box: HTMLElement, f: File): Promise<void> {
  if (/\.sqg$/i.test(f.name)) return readBackup(box, f)
  if (f.size > MAX_BYTES) {
    showChoose(box, 'That file is too big to read here (over 8 MB). Save just the customers as a CSV file and try again.')
    return
  }
  fileStatus(box, `Reading ${f.name}…`)
  let sheets: SheetData[]
  try {
    if (/\.(csv|tsv|txt)$/i.test(f.name) || f.type === 'text/csv') {
      sheets = [{ name: '', rows: parseCsv(await readText(f)) }]
    } else {
      // Excel and friends: the reader is only downloaded when it is needed.
      const XLSX = await import('xlsx')
      const book = XLSX.read(await f.arrayBuffer(), { type: 'array', cellDates: false, dateNF: 'yyyy-mm-dd' })
      sheets = book.SheetNames.map((name) => ({
        name,
        rows: tidyRows(XLSX.utils.sheet_to_json<unknown[]>(book.Sheets[name], { header: 1, raw: false, defval: '' })),
      })).filter((x) => x.rows.length > 0)
      // The sheet that reads most like a customer list goes first.
      sheets.sort((a, b) => sheetScore(b.rows) - sheetScore(a.rows))
    }
  } catch {
    showChoose(box, "Roundly couldn't read that file. Save it as a CSV or Excel file and try again.")
    return
  }
  if (sheets.length === 0 || sheets[0].rows.length === 0) {
    showChoose(box, 'That file looks empty.')
    return
  }
  useSheet(box, f.name, sheets, 0)
}

// Squeegee's full backup (.sqg; squeegee-backup.ts): opened here in the browser, its customers turned
// into the rows of Squeegee's All_Jobs.csv, then checked and imported the same way. The opener is only
// downloaded when a backup is chosen. One that can't be opened says so, and how to send it to us.
async function readBackup(box: HTMLElement, f: File): Promise<void> {
  if (f.size > MAX_BACKUP_BYTES) {
    showChoose(box, `That backup is too big to open here (over ${MAX_BACKUP_BYTES / (1024 * 1024)} MB). Choose Squeegee's All_Jobs.csv instead, or email the backup to hello@roundly.work.`)
    return
  }
  fileStatus(box, `Opening ${f.name}…`)
  let got: { rows: string[][]; notIn: { name: string; reason: string }[] }
  let money: MoneyState | undefined
  try {
    const [backup, { XzReadableStream }] = await Promise.all([import('../squeegee-backup'), import('xz-decompress')])
    const unxz = (xz: Uint8Array): ReadableStream<Uint8Array> => new XzReadableStream(new Blob([xz as BlobPart]).stream())
    try {
      {
        // Every owner's backup (0043, release step 6): the same customers, and each one's money owed or credit, with
        // what is already on the round from an earlier backup. A round whose money from Squeegee is still off sees the
        // same money check; accepting it is what switches it on (send, money_on).
        const { data, madeAt } = await backup.readBackupData(new Uint8Array(await f.arrayBuffer()), unxz)
        got = backup.backupSheet(data, todayISO())
        const [{ squeegeeMoney }, known] = await Promise.all([
          import('../squeegee-money'),
          // No signal for this: the server still never adds an account twice.
          api<{ accounts: KnownAccount[] }>('/api/brought-accounts').then((r) => r.accounts, () => [] as KnownAccount[]),
        ])
        const round = getMe()?.round
        const m = squeegeeMoney(data, { asOf: madeAt, currency: round?.currency ?? 'GBP', known: known.map((k) => ({ squeegee_ref: k.squeegee_ref, squeegee_id: k.squeegee_id, accepted_pence: k.accepted_pence })) })
        if (moneyToCheck(m)) {
          money = { m, answers: {}, refused: moneyRefusal(madeAt, todayISO(), round?.timeZone ?? 'Europe/London'), known }
        }
      }
    } catch {
      showChoose(box, backup.BACKUP_UNREADABLE)
      return
    }
  } catch {
    // The opener itself didn't download (no signal).
    showChoose(box, "Roundly couldn't open that backup just now. Check your signal and choose it again.")
    return
  }
  // Everyone stopped in Squeegee, but some still owe or have credit: their money can come in on its own, each of them
  // as Lapsed (Astra, steps 5-6 finding 7).
  // So can a reimport whose customers are all here already: their newer figures, and any history still to come (that
  // even from a backup too old for money; short check finding 5).
  const moneyAlone = !!money && ((!money.refused && moneyOnly(money.m)) || resumeHistory(money).length > 0)
  if (got.rows.length <= 1 && !moneyAlone) {
    showChoose(box, got.notIn.length
      ? `Every customer in that backup is stopped or archived in Squeegee, so there is nobody to bring in (${got.notIn.slice(0, 5).map((c) => c.name).join(', ')}${got.notIn.length > 5 ? '…' : ''}).`
      : "That Squeegee backup has no customers in it. In Squeegee's Manage Backups, download the newest backup and choose that one.")
    return
  }
  useSheet(box, f.name, [{ name: '', rows: got.rows, notIn: got.notIn, ...(money ? { money } : {}) }], 0)
}

// A CSV saved by Excel on Windows is often not UTF-8 (a £ reads as "�"):
// read it as UTF-16 when it says so, or as Windows-1252 when UTF-8 fails.
async function readText(f: File): Promise<string> {
  const bytes = new Uint8Array(await f.arrayBuffer())
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes)
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes)
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('windows-1252').decode(bytes)
  }
}

// The customers' rows under the headings row: Squeegee's job export with each customer's jobs together
// (one row per job there; squeegeeJobs in import-sheet.ts), any other sheet as it is.
function customerRows(rows: string[][], h: number, keep?: Set<number>): { body: string[][]; lines: number[]; above: number[]; joined: Sheet['joined'] } {
  const got = bodyRows(rows, h, keep)
  const header = h >= 0 ? rows[h] : null
  if (!header || !isSqueegeeJobs(header)) return { ...got, joined: [] }
  const jobs = squeegeeJobs(header, got.body, got.lines, todayISO())
  return { body: jobs.body, lines: jobs.lines, above: got.above, joined: jobs.joined }
}

// `forcedHeader`: the owner's answer to "Which row has your column names?"
// (-1 for none).
function useSheet(box: HTMLElement, file: string, sheets: SheetData[], index: number, forcedHeader?: number): void {
  const rows = sheets[index].rows
  const choice = headerChoice(rows)
  const h = forcedHeader ?? choice.index
  const header = h >= 0 ? rows[h] : null
  const { body, lines, above, joined } = customerRows(rows, h)
  if (body.length > MAX_CUSTOMERS) {
    showChoose(box, `That file has ${body.length} rows. Roundly imports up to ${MAX_CUSTOMERS} at a time: split it and import each part.`)
    return
  }
  const fields = guessFields(header, body)
  sheet = { file, header, body, lines, above, joined, keepAbove: new Set(), firstLine: h + 2, fields, sheets, sheetName: sheets[index].name || null, sheetIndex: index, headerRow: h, headerUnsure: forcedHeader === undefined && choice.unsure,
    firstFields: [...fields], headerGuessed: choice.index, headerUnsureAtStart: choice.unsure, guessedAtStart: guessedColumns(header, fields).length }
  resetChoices()
  void showCheck(box)
}

async function showCheck(box: HTMLElement): Promise<void> {
  if (!sheet) return
  const gen = routeGen
  if (!existingKeys) {
    try {
      const data = await api<{ customers: Customer[] }>('/api/customers', 'customers')
      existingKeys = new Set(data.customers.map((c) => sameCustomerKey(c)))
      // Two customers here with the same key are never matched: which one a
      // sheet row means is not certain (Codex review 2026-09-28).
      existingByKey = new Map()
      const twice = new Set<string>()
      for (const c of data.customers) {
        const k = sameCustomerKey(c)
        if (existingByKey.has(k)) twice.add(k)
        existingByKey.set(k, c)
      }
      for (const k of twice) existingByKey.delete(k)
      dupCheckFailed = false
    } catch {
      dupCheckFailed = true
    }
  }
  if (gen !== routeGen) return
  drawCheck(box)
}

// A new customer on the check screen: after the owner's changes, with its
// spreadsheet row number and what it is missing.
// twice: the row is the same customer as an earlier row of the file, so it
// is left out unless the owner ticks it.
// priceWords: the sheet's price when it did not say one clear amount ("£20/£25").
interface Person { row: ImportRow; line: number; nameIsAddress: boolean; missing: MissingDetail[]; twice: boolean; priceWords?: string }
const MISSING_WORDS: Record<MissingDetail, string> = { address: 'address', phone: 'phone number', price: 'price', cycle: 'how often' }
// Each list shows this many until "Show the other …".
const LIST_LIMIT = 25

function everyLabel(days: number): string {
  if (days === 7) return 'every week'
  if (days % 7 === 0 && days <= 112) return `every ${days / 7} weeks`
  const label = cycleLabel(days)
  return label === 'yearly' ? 'once a year' : label === 'monthly' ? 'every month' : `every ${label}`
}

function drawCheck(box: HTMLElement): void {
  importStage('check')
  const s = sheet!
  clear(box)
  const today = todayISO()
  const names = new Map<number, string>()
  for (const [line, e] of edits) if (e.name) names.set(line, e.name)
  // A UK round reads "03/04" day first, as always. A US or Canadian round goes by the sheet's own dates when they say
  // ("03/25" can only be month first), else the owner's answer, else the country's habit (the US month first).
  const orderCells = roundConfig().country === 'GB' ? [] : numericDateCells(s.body, s.fields)
  const sheetOrder = numericDateOrder(orderCells)
  const dateOrder: DateOrder = roundConfig().country === 'GB' ? 'dmy' : dateOrderChosen ?? sheetOrder ?? (roundConfig().country === 'US' ? 'mdy' : 'dmy')
  const built = buildCustomers(s.body, s.fields, s.firstLine, s.header, { yearMode, today, names, sections: !sectionsOff, lines: s.lines, dateOrder, monthly: monthlyCycles() })
  const onRound = existingKeys ?? new Set<string>()
  const inFile = new Set<string>()
  const fresh: Person[] = []
  // Customers already here whose sheet shows a later visit than Roundly has:
  // only a sure match (one customer with that key, and the same address when
  // both have one), and only the latest visit per customer.
  const byCustomer = new Map<number, { customer_id: number; name: string; date: string; amount_pence?: number }>()
  const sameAddress = (a?: string | null, b?: string | null): boolean => !a || !b || a.toLowerCase().replace(/[^a-z0-9]/g, '') === b.toLowerCase().replace(/[^a-z0-9]/g, '')
  let unsure = 0
  let already = 0
  let twice = 0
  // Money from a Squeegee backup (0043, release step 6): each row's Squeegee reference and the house it is for, so
  // each account goes onto the customer that holds it (new, or already here).
  const sheetMoney = s.sheets[s.sheetIndex].money
  const col = (name: string): number => s.header?.indexOf(name) ?? -1
  const [refCol, jobAddrCol, addrCol] = [col('Customer Ref'), col('Job Address'), col('Address')]
  const rawOf = (line: number): string[] | undefined => s.body[s.lines.indexOf(line)]
  const refOf = (line: number): string => (refCol >= 0 ? rawOf(line)?.[refCol]?.trim() ?? '' : '')
  const houseOf = (line: number): string => {
    const r = rawOf(line)
    return (jobAddrCol >= 0 ? r?.[jobAddrCol]?.trim() : '') || (addrCol >= 0 ? r?.[addrCol]?.trim() ?? '' : '')
  }
  const existingMoney: Existing[] = []
  built.rows.forEach((raw, i) => {
    const { line, nameIsAddress, nameTyped, priceWords } = built.meta[i]
    // Matched on the sheet's own details, before the owner's changes: a change
    // never turns a new customer into someone already on the round, nor hands
    // their last visit to that customer (Astra review 28 Sep 2026).
    const k = sameCustomerKey(raw)
    if (!nameTyped && onRound.has(k)) {
      already++
      const c = existingByKey.get(k)
      if (sheetMoney && c && sameAddress(c.address, raw.address)) existingMoney.push({ customer_id: c.id, ref: refOf(line), address: houseOf(line) })
      if (!raw.last_visit) return
      if (!c || !sameAddress(c.address, raw.address)) { unsure++; return }
      if (c.last_clean && c.last_clean >= raw.last_visit.date) return
      const had = byCustomer.get(c.id)
      if (!had || had.date < raw.last_visit.date) byCustomer.set(c.id, { customer_id: c.id, name: c.name, ...raw.last_visit })
      return
    }
    const edit = edits.get(line)
    const r = applyEdit(raw, edit)
    const named = nameIsAddress && !(edit && 'address' in edit)
    const again = inFile.has(k)
    inFile.add(k)
    if (again) twice++
    fresh.push({ row: r, line, nameIsAddress: named, missing: missingDetails(r, named), twice: again, priceWords })
  })
  const attach = [...byCustomer.values()]
  // The owner's tick wins; untouched, a second copy starts left out.
  const isOut = (p: Person): boolean => (picked.has(p.line) ? !picked.get(p.line) : p.twice)
  const ticked = (): Person[] => fresh.filter((p) => !isOut(p))
  const withVisits = fresh.filter((p) => p.row.last_visit).length + attach.length
  const hasVisitCols = s.fields.includes('visit_date')
  // "Is this sheet up to date?" comes before the other questions about visits.
  const oldSheet = hasVisitCols && withVisits > 0 && sheetLooksOld(built.visits.to, today)
  const askSheet = oldSheet && sheetAnswer === null

  const colName = (c: number): string => s.header?.[c]?.trim() || `Column ${String.fromCharCode(65 + (c % 26))}${c >= 26 ? Math.floor(c / 26) : ''}`
  // "Which row has your column names?": a list of the first rows, and None.
  const headerPick = (): HTMLSelectElement => {
    const rows = s.sheets[s.sheetIndex].rows
    const pick = el('select', { 'aria-label': 'Which row has your column names' }) as HTMLSelectElement
    pick.append(el('option', { value: '-1' }, 'None: my sheet has no column names'))
    rows.slice(0, 10).forEach((r, i) => pick.append(el('option', { value: String(i) }, `Row ${i + 1}: ${r.filter((x) => x.trim()).slice(0, 4).join(', ').slice(0, 60)}`)))
    pick.value = String(s.headerRow)
    pick.addEventListener('change', () => useSheet(box, s.file, s.sheets, s.sheetIndex, Number(pick.value)))
    return pick
  }
  let headerCard: HTMLElement | null = null
  if (s.headerUnsure) {
    headerCard = el('section', { class: 'import-card import-old' })
    const yes = el('button', { type: 'button', class: 'btn-secondary' }, s.headerRow >= 0 ? "That's right" : 'It has none')
    yes.addEventListener('click', () => { s.headerUnsure = false; drawCheck(box) })
    headerCard.append(
      el('strong', {}, 'Which row has your column names?'),
      el('p', {}, s.headerRow >= 0 ? `Roundly thinks it is row ${s.headerRow + 1}. If that is wrong, pick the right one.` : "Roundly couldn't find a row of column names. If your sheet has one, pick it."),
      el('label', { class: 'import-year' }, headerPick()),
    )
    // Rows above the column names: left out unless ticked (most are notes).
    if (s.above.length) {
      const rowsAll = s.sheets[s.sheetIndex].rows
      const q = el('fieldset', { class: 'import-paid' })
      q.append(el('legend', {}, 'Rows above them are left out. Tick any that are customers:'))
      for (const i of s.above) {
        const box2 = el('input', { type: 'checkbox' }) as HTMLInputElement
        box2.checked = s.keepAbove.has(i)
        box2.addEventListener('change', () => {
          if (box2.checked) s.keepAbove.add(i)
          else s.keepAbove.delete(i)
          const got = customerRows(rowsAll, s.headerRow, s.keepAbove)
          s.body = got.body
          s.lines = got.lines
          s.joined = got.joined
          drawCheck(box)
        })
        q.append(el('label', { class: 'import-paid-choice' }, box2, ` Row ${i + 1}: ${rowsAll[i].filter((c) => c.trim()).join(', ').slice(0, 70)}`))
      }
      headerCard.append(q)
    }
    headerCard.append(el('div', { class: 'import-actions' }, yes))
  }

  // What each column goes into.
  const cols = el('section', { class: 'import-card' })
  cols.append(el('h3', {}, 'Check what goes where'), el('p', { class: 'muted' }, `From ${s.file}. Change any that are wrong.`))
  const list = el('div', { class: 'import-cols' })
  s.fields.forEach((f, c) => {
    // Two customers' values, labelled as examples and kept apart: joined by a
    // dot they read as one customer ("Melis & Emra Kahyaoglu · Joel", Liam
    // 28 Sep 2026).
    const samples = s.body.map((r) => r[c]).filter(Boolean).slice(0, 2)
    const example = samples.length ? `e.g. ${samples.map((v) => `"${v}"`).join(', then ')}` : ''
    const heading = s.header?.[c]?.trim() || `Column ${String.fromCharCode(65 + (c % 26))}${c >= 26 ? Math.floor(c / 26) : ''}`
    const select = el('select', { 'aria-label': `What "${heading}" goes into` }) as HTMLSelectElement
    for (const [value, label] of Object.entries(FIELD_LABELS)) select.append(el('option', { value }, postcodeWords(label)))
    select.value = f
    select.addEventListener('change', () => {
      s.fields[c] = select.value as ImportField
      // A date column put back by hand after "Leave the dates out": ask again.
      if (select.value === 'visit_date' && sheetAnswer === 'out') { sheetAnswer = null; fieldsBeforeOut = null; newestWhenOut = null }
      drawCheck(box)
    })
    // Why a past-visits column is left out: only each customer's last visit
    // comes in (28 Sep 2026), or none when the dates were left out.
    const history = f === 'skip' && !!s.header?.[c] && isHistoryHeading(s.header[c]!)
    const why = !history ? '' : hasVisitCols ? "Only each customer's last visit comes in, with its price if it has one. Older visits are left out for now." : sheetAnswer === 'out' ? 'Left out: you chose to leave the dates out.' : 'Past visits: left out for now. Your customers come in without their history.'
    list.append(
      el('div', { class: f === 'skip' ? 'import-col import-col-skip' : 'import-col' }, el('div', { class: 'import-col-words' }, el('strong', {}, heading), el('span', { class: 'muted' }, example || 'empty'),
        why ? el('span', { class: 'import-col-why' }, why) : ''), select),
    )
  })
  cols.append(el('label', { class: 'import-sheet-pick' }, 'Row with your column names ', headerPick()), list)

  // The Add button and why it is waiting, kept up to date as customers are ticked.
  const areasToAsk = sectionsAnswer === null && built.lone.length > 0
  const tie = priceAnswered ? [] : priceTie(s.header, s.body)
  const region = regionAnswered ? -1 : regionQuestion(s.header, s.body, s.fields)
  const nameOrder = nameOrderQuestion(s.header, s.fields)
  // Not asked again for the name columns the first-name question covers.
  const guessesConfirmed = guessesConfirmedFor === JSON.stringify(s.fields)
  const guessed = guessesConfirmed ? [] : guessedColumns(s.header, s.fields).filter((c) => !nameOrder.includes(c))
  const yearRolledBack = !!built.visits.from && Number(built.visits.from.slice(0, 4)) < Number(today.slice(0, 4))
  const needsYear = withVisits > 0 && built.visits.yearless > 0 && !yearChosen && yearRolledBack
  // Money from a Squeegee backup: each account with the customer that holds it (release step 6). A last visit of a
  // customer whose account comes with them is in their opening balance (carryover), so "Were these last visits paid?"
  // is not asked when every one of them is (Squeegee's figure already says what is owed).
  const placedNow = (): Placed | null => (sheetMoney && !sheetMoney.refused ? placeMoney(sheetMoney, settledMoney(sheetMoney), ticked().map((p): MoneyPerson => ({ line: p.line, ref: refOf(p.line), address: houseOf(p.line) })), existingMoney) : null)
  const visitsCovered = (() => {
    const placed = placedNow()
    if (!placed) return false
    const withAccount = new Set([...placed.accounts.map((a) => a.customer_id), ...sheetMoney!.known.map((k) => k.customer_id)])
    return ticked().every((p) => !p.row.last_visit || placed.rows.has(p.line)) && attach.every((a) => withAccount.has(a.customer_id))
  })()
  const needsPaid = withVisits > 0 && visitsPaid === null && !visitsCovered
  const selectedCount = el('strong', { class: 'import-selected-count' })
  const missingCount = el('span')
  const summary = el('section', { class: 'import-card import-review-summary' },
    el('span', { class: 'import-eyebrow' }, 'Check before adding'),
    el('h3', {}, 'Your file is ready to review'),
    el('p', { class: 'muted import-filename' }, s.file),
    el('div', { class: 'import-review-counts' }, selectedCount,
      missingCount,
      el('span', {}, `${already} already on your round`)),
    el('p', {}, 'Nothing has been added yet. Check the questions below, then review your customers.'),
    dupCheckFailed ? el('p', { class: 'form-error', role: 'alert' }, 'Could not check existing customers. Check your signal and choose the file again to avoid duplicates.') : '')
  const go = el('button', { type: 'button', class: 'btn-primary import-go' })
  const why = el('p', { class: 'muted import-why' })
  // Add stays off until every money question is answered (PLAN section 4), unless this backup's money can't come in.
  const moneyWaiting = (): boolean => !!sheetMoney && !sheetMoney.refused && !settledMoney(sheetMoney).ready
  const refreshGo = (): void => {
    const n = ticked().length
    selectedCount.textContent = `${n} ${n === 1 ? 'customer selected' : 'customers selected'}`
    missingCount.textContent = `${ticked().filter(p => p.missing.length > 0).length} selected with details to fill in`
    // Money for customers already here (an account, Lapsed ones, or a newer backup's differences) is something to add too.
    const placed = n === 0 && attach.length === 0 ? placedNow() : null
    const moneyCount = placed ? placed.accounts.length + placed.lapsed.length + placed.corrections.length : 0
    // History still to arrive for accounts already here is something to send too (finding 4).
    const resumeCount = n === 0 && attach.length === 0 && moneyCount === 0 && sheetMoney ? resumeHistory(sheetMoney).length : 0
    const nothing = n === 0 && attach.length === 0 && moneyCount === 0 && resumeCount === 0
    go.textContent = nothing ? 'Nothing to add'
      : resumeCount ? `Finish bringing in their history from Squeegee`
      : n === 0 && attach.length === 0 ? `Bring in the money for ${moneyCount} ${moneyCount === 1 ? 'customer' : 'customers'}`
      : n === 0 ? `Add last visits to ${attach.length} ${attach.length === 1 ? 'customer' : 'customers'}` : n === 1 ? 'Add 1 customer' : `Add ${n} customers`
    const reason = nothing ? ''
      : openLine !== null ? "Save or cancel the customer you're changing first."
      : s.headerUnsure ? 'Answer "Which row has your column names?" first.'
      : areasToAsk ? 'Answer "Are these areas or customers?" first.'
      : tie.length ? 'Answer "Which column is the regular price?" first.'
      : region >= 0 ? `Answer "Is '${colName(region)}' part of the address or the area?" first.`
      : nameOrder.length ? 'Answer "Which of these is the first name?" first.'
      : guessed.length ? 'Check "Is this right?" first.'
      : askSheet ? 'Answer "Is this sheet up to date?" first.'
      : needsYear ? 'Say which year the dates with no year are from first.'
      : needsPaid ? 'Answer "Were these last visits paid?" first.'
      : moneyWaiting() ? 'Answer the money questions first.'
      : ''
    go.toggleAttribute('disabled', nothing || !!reason)
    why.textContent = reason
    why.hidden = !reason
  }
  go.addEventListener('click', () => {
    sendFeedback(s, { rows: fresh.length, added: ticked().length, edited: edits.size, unticked: fresh.length - ticked().length })
    // A next visit says where it came from, on the visit itself.
    const from = s.header && isSqueegeeJobs(s.header) ? 'Booked from Squeegee' : 'Booked from your import'
    // Money (release step 6): each account with the customer that holds it, accounts for customers already here,
    // stopped customers brought in as Lapsed, and a newer backup's differences.
    const placed = placedNow()
    const out: SendRow[] = ticked().map(({ line, row: { last_visit, next_visit, ...r } }) => ({
      ...r,
      ...(last_visit ? { last_visit: { ...last_visit, paid: visitsPaid === true } } : {}),
      ...(next_visit ? { next_visit: { ...next_visit, note: from } } : {}),
      ...(placed?.rows.has(line) ? { brought: placed.rows.get(line)! } : {}),
    }))
    if (placed) out.push(...placed.lapsed)
    void send(box, out, attach.map(({ name: _n, ...a }) => ({ ...a, paid: visitsPaid === true })), placed, sheetMoney ? resumeHistory(sheetMoney) : [])
  })

  // "Money owed and credit" from a Squeegee backup (PLAN section 4, release step 6): the totals Squeegee shows and
  // what Roundly will show, owed and credit apart; the switch-day freeze; every question, with "same answer for all"
  // per kind; and who is not brought. Each answer redraws the screen, so the totals and Add follow it.
  let moneyCard: HTMLElement | null = null
  if (sheetMoney) {
    const st = settledMoney(sheetMoney)
    const zone = getMe()?.round?.timeZone ?? 'Europe/London'
    moneyCard = el('section', { class: 'import-card import-money' })
    moneyCard.append(el('h3', {}, 'Money owed and credit'))
    if (sheetMoney.refused) {
      moneyCard.append(el('p', { class: 'import-money-refused' }, sheetMoney.refused))
    } else {
      moneyCard.append(...totalsWords(sheetMoney, st, zone).map((t, i) => el('p', i === 0 ? { class: 'import-money-lead' } : {}, t)))
      moneyCard.append(
        el('p', { class: 'import-money-freeze' }, "Don't take money or mark work done in Squeegee from now on."),
        el('p', { class: 'muted' }, 'Check it in Squeegee: Insights, Reporting, Customer Balances for Date.'),
      )
      const answer = (qid: string, value: string): void => {
        sheetMoney.answers[qid] = value
        drawCheck(box)
      }
      const kinds = [...new Set(sheetMoney.m.questions.map((q) => q.kind))]
      for (const kind of kinds) {
        const qs = sheetMoney.m.questions.filter((q) => q.kind === kind)
        const group = el('fieldset', { class: 'import-money-group' })
        group.append(el('legend', {}, `${KIND_WORDS[kind]} (${qs.length})`))
        // "Same answer for all": the answers every one of them offers.
        const common = qs[0].options.filter((o) => !o.startsWith('house:') && qs.every((q) => q.options.includes(o)))
        if (qs.length > 1 && common.length) {
          const all = el('select', { 'aria-label': `Same answer for all: ${KIND_WORDS[kind]}` }) as HTMLSelectElement
          all.append(el('option', { value: '' }, 'Same answer for all…'), ...common.map((o) => el('option', { value: o }, optionWords(qs[0], o))))
          all.addEventListener('change', () => {
            if (!all.value) return
            sheetMoney.answers = answerAll(sheetMoney, kind, all.value)
            drawCheck(box)
          })
          group.append(el('label', { class: 'import-money-all' }, all))
        }
        for (const q of qs) group.append(moneyQuestion(q, sheetMoney.answers[q.id] ?? q.default, answer))
        moneyCard.append(group)
      }
      if (st.too_many.length) moneyCard.append(el('p', { class: 'form-error' }, `More direct debits still collecting than Roundly can bring for ${st.too_many.join(', ')}: send us the file below and we'll bring the money in for you.`))
      if (st.invalid.length) moneyCard.append(el('p', { class: 'form-error' }, 'A direct debit answer above leaves it as something that is not a payment. Answer it again.'))
      const notBrought = sheetMoney.m.not_brought.filter((n) => n.pence !== 0)
      if (notBrought.length) {
        moneyCard.append(el('p', { class: 'muted' }, `Not brought: ${notBrought.slice(0, 6).map((n) => `${n.name || 'someone not in the file'} (${n.pence > 0 ? `${pounds(n.pence)} owed` : `${pounds(n.pence)} credit`}, ${n.reason === 'deleted' ? 'deleted in Squeegee' : 'not in the file'})`).join(', ')}${notBrought.length > 6 ? '…' : ''}.`))
      }
    }
  }

  // Every new customer, one line each (Liam 28 Sep 2026: "an easy way to
  // approve customers at a glance"): ticked unless the owner unticks them, the
  // incomplete ones first, and a tap opens a customer's details for changing.
  const focusRow = (line: number): void => {
    box.querySelector<HTMLElement>(`.import-person[data-line="${line}"] .import-person-open`)?.focus()
  }
  const personRow = (p: Person): HTMLElement => {
    const r = p.row
    const item = el('div', { class: `import-person${isOut(p) ? ' is-out' : ''}`, 'data-line': String(p.line) })
    const tick = el('input', { type: 'checkbox', 'aria-label': `Add ${r.name}` }) as HTMLInputElement
    tick.checked = !isOut(p)
    tick.addEventListener('change', () => {
      picked.set(p.line, tick.checked)
      item.classList.toggle('is-out', !tick.checked)
      refreshGo()
    })
    item.append(el('label', { class: 'import-tick' }, tick))
    if (openLine === p.line) {
      item.append(editForm(p))
      return item
    }
    const price = r.reg_price_pence !== undefined ? money(r.reg_price_pence) : ''
    // "£20.00 every 4 weeks", or "£30.00 · ad-hoc" for an answer that is not a cycle.
    const often = r.cycle_days ? [price, everyLabel(r.cycle_days)].filter(Boolean).join(' ') : [price, r.cycle_text ? cycleLabel(null, r.cycle_text) || r.cycle_text : ''].filter(Boolean).join(' · ')
    const where = addressLine(r.address, r.postcode, ', ') || (r.maps_link ? 'Google Maps pin' : '')
    const summary = [often, where, r.phone ?? '', r.area ?? ''].filter(Boolean).join(' · ')
    const open = el('button', { type: 'button', class: 'import-person-open', title: 'Change their details' }, el('strong', {}, r.name))
    if (summary) open.append(el('span', {}, summary))
    // The visit their old app had arranged, booked as they come in (Squeegee's Next Due, 2 Oct 2026).
    const booking = r.next_visit ? `Next visit booked: ${dayLabel(r.next_visit.date)}` : ''
    if (r.last_visit) {
      const visit = el('span', {}, `Last clean ${shortDate(r.last_visit.date)}${r.last_visit.amount_pence !== undefined ? ` (${money(r.last_visit.amount_pence)})` : ''}`)
      if (booking) visit.append(` · ${booking}`)
      else if (r.cycle_days) {
        const due = cyclesOn(r.last_visit.date, r.cycle_days)
        visit.append(' · ', due < today ? el('span', { class: 'import-late' }, `due since ${shortDate(due)}`) : `next due ${shortDate(due)}`)
      }
      open.append(visit)
    } else if (booking) open.append(el('span', {}, booking))
    // An unclear price says what the sheet had: "price (your sheet says "£20/£25")".
    const said = (m: MissingDetail): string => (m === 'price' && p.priceWords ? `price (your sheet says "${p.priceWords}")` : MISSING_WORDS[m])
    if (p.missing.length) open.append(el('span', { class: 'import-missing' }, `Missing: ${p.missing.map(said).join(', ')}`))
    if (p.twice) open.append(el('span', { class: 'import-missing' }, 'In your file twice, so left out unless you tick it.'))
    open.addEventListener('click', () => {
      openLine = p.line
      drawCheck(box)
    })
    item.append(open)
    return item
  }
  // Only what the owner changed is kept, so a column changed afterwards still
  // fills in the rest of this customer.
  const editForm = (p: Person): HTMLElement => {
    const r = p.row
    const form = el('form', { class: 'import-edit' })
    const field = (label: string, value: string, attrs: Record<string, string> = {}): HTMLInputElement => {
      const input = el('input', { type: 'text', autocomplete: 'off', ...attrs }) as HTMLInputElement
      input.value = value
      form.append(el('label', { class: 'import-edit-field' }, el('span', {}, label), input))
      return input
    }
    const name = field('Name', r.name, { maxlength: '200' })
    const address = field('Address', r.address ?? (p.nameIsAddress ? r.name : ''), { maxlength: '500' })
    const postcode = field(postcodeWords('Postcode'), r.postcode ?? '', { maxlength: '20', autocapitalize: 'characters' })
    const phone = field('Phone', r.phone ?? '', { maxlength: '100', inputmode: 'tel' })
    const price = field(`Price (${currencySymbol()})`, r.reg_price_pence !== undefined ? (r.reg_price_pence / 100).toFixed(2) : '', { inputmode: 'decimal' })
    const cycle = el('select', {}) as HTMLSelectElement
    cycleOptions().forEach((o, i) => cycle.append(el('option', { value: String(i) }, o.label)))
    let chosen = cycleValueFor(r.cycle_days, r.cycle_text)
    if (r.cycle_days && !cycleOptions().some((o) => o.days === r.cycle_days)) {
      cycle.append(el('option', { value: `d${r.cycle_days}` }, cycleLabel(r.cycle_days, null)))
      chosen = `d${r.cycle_days}`
    } else if (!chosen && r.cycle_text) {
      // Their own words ("on request"), kept unless another choice is made.
      cycle.append(el('option', { value: 'keep' }, `"${r.cycle_text}"`))
      chosen = 'keep'
    }
    cycle.value = chosen || '0'
    form.append(el('label', { class: 'import-edit-field' }, el('span', {}, 'How often'), cycle))
    const err = el('p', { class: 'form-error', role: 'alert' })
    const save = el('button', { type: 'submit', class: 'btn-primary' }, 'Save')
    const cancel = el('button', { type: 'button', class: 'btn-secondary' }, 'Cancel')
    form.append(err, el('div', { class: 'import-actions' }, save, cancel))
    const close = (): void => {
      openLine = null
      lastChanged = p.line
      drawCheck(box)
      focusRow(p.line)
    }
    cancel.addEventListener('click', close)
    form.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); close() } })
    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const n = name.value.trim()
      if (!n) { err.textContent = 'Put in a name.'; name.focus(); return }
      let pence: number | null
      try {
        pence = parseMoney(price.value)
      } catch {
        err.textContent = `Put the price in ${currencyWord()}, e.g. 25 or 25.50`
        price.focus()
        return
      }
      if (pence !== null && pence > 10_000_00) { err.textContent = `That is over ${currencySymbol()}10,000. Check the number.`; price.focus(); return }
      const next: RowEdit = {
        name: n,
        address: address.value.trim() || null,
        postcode: postcode.value.trim().toUpperCase() || null,
        phone: phone.value.trim() || null,
        reg_price_pence: pence,
      }
      const v = cycle.value
      if (v.startsWith('d')) {
        next.cycle_days = Number(v.slice(1))
        next.cycle_text = null
      } else if (v !== 'keep') {
        const o = cycleOptions()[Number(v)]
        next.cycle_days = o?.days ?? null
        next.cycle_text = o && !o.days ? o.marker ?? null : null
      }
      const edit: RowEdit = { ...edits.get(p.line) }
      for (const k of EDIT_KEYS) {
        if (!(k in next)) continue
        const now = next[k] ?? null
        if (now !== (r[k] ?? null)) (edit as Record<string, unknown>)[k] = now
      }
      edits.set(p.line, edit)
      close()
    })
    return form
  }

  const people = el('section', { class: 'import-card import-people' })
  people.append(el('h3', {}, fresh.length === 0 ? 'Nothing new to add' : fresh.length === 1 ? 'Your customer' : `Your ${fresh.length} customers`))
  if (fresh.length === 0) people.append(el('p', { class: 'muted' }, already > 0 ? 'Everyone in this file is already on your round.' : 'No customers found. Check the Name column below.'))
  else people.append(el('p', { class: 'muted' }, "Untick anyone you don't want. Tap a customer to change their details."))
  const group = (key: keyof typeof showAll, title: string, sub: string | null, members: Person[]): void => {
    if (!members.length) return
    // The customer just changed stays in view, even past the first ones shown.
    const shown = showAll[key] ? members : members.filter((p, i) => i < LIST_LIMIT || p.line === lastChanged || p.line === openLine)
    people.append(el('h4', { class: `import-group import-group-${key}` }, `${title} (${members.length})`))
    if (sub) people.append(el('p', { class: 'muted import-group-sub' }, sub))
    people.append(el('div', { class: 'import-people-list' }, ...shown.map(personRow)))
    if (shown.length < members.length) {
      const more = el('button', { type: 'button', class: 'link-button' }, `Show the other ${members.length - shown.length}`)
      more.addEventListener('click', () => { showAll[key] = true; drawCheck(box) })
      people.append(more)
    }
  }
  const incomplete = fresh.filter((p) => !p.twice && p.missing.length > 0)
  const ready = fresh.filter((p) => !p.twice && p.missing.length === 0)
  group('incomplete', 'Details to fill in', 'They still come in. Fill in the gaps now, or later on their page.', incomplete)
  group('ready', 'Ready', null, ready)
  group('duplicate', 'In your file twice', 'The extra copy starts unticked. Only tick it if it is a different customer.', fresh.filter(p => p.twice))

  const notes: string[] = []
  if (dupCheckFailed) notes.push("Roundly couldn't check who is already on your round, so anyone already there may be added twice. Check your signal and choose the file again.")
  if (already > 0) notes.push(`${already} ${already === 1 ? 'is' : 'are'} already on your round and will be left out.`)
  // Squeegee lists each job on its own row (2 Oct 2026): said once, with who.
  if (s.joined.length) {
    const who = s.joined.slice(0, 4).map((j) => `${j.name} (${j.jobs} jobs)`).join(', ')
    notes.push(`Squeegee lists each job on its own row. ${s.joined.length === 1 ? 'This customer has' : `${s.joined.length} customers have`} more than one, so each comes in once with the jobs together: ${who}${s.joined.length > 4 ? '…' : ''}. Jobs done on the same visit have their prices added; any other job is in their notes.`)
  }
  if (twice > 0) notes.push(`${twice} ${twice === 1 ? 'customer is' : 'customers are'} in your file twice. The second copy is left out unless you tick it.`)
  // From a Squeegee backup: the customers stopped or archived there are not coming in, each with why.
  const notIn = s.sheets[s.sheetIndex].notIn ?? []
  if (notIn.length) {
    const who = notIn.slice(0, 6).map((c) => `${c.name} (${c.reason})`).join(', ')
    notes.push(`${notIn.length === 1 ? '1 customer is' : `${notIn.length} customers are`} not coming in: ${who}${notIn.length > 6 ? '…' : ''}. Add them by hand if you still clean for them.`)
  }
  // Rows left out, each kind said plainly (Codex review 28 Sep 2026).
  const rowsOf = (xs: { line: number }[]): string => `row ${xs.slice(0, 5).map((l) => l.line).join(', ')}${xs.length > 5 ? '…' : ''}`
  const noName = built.left.filter((l) => l.reason === 'no name or address')
  const totals = built.left.filter((l) => l.reason === 'a totals row')
  if (noName.length) notes.push(`${noName.length} ${noName.length === 1 ? 'row has' : 'rows have'} no name or address and will be left out (${rowsOf(noName)}).`)
  const areaRows = built.left.filter((l) => l.reason === 'an area heading row')
  if (areaRows.length) notes.push(`${areaRows.length} ${areaRows.length === 1 ? 'row has' : 'rows have'} only an area in them ("${areaRows[0].text}") and ${areaRows.length === 1 ? 'is' : 'are'} left out (${rowsOf(areaRows)}).`)
  const again = built.left.filter((l) => l.reason === 'the headings again')
  if (again.length) notes.push(`Your column names appear again further down, so ${again.length === 1 ? 'that row is' : 'those rows are'} left out (${rowsOf(again)}).`)
  if (totals.length) notes.push(`${totals.length === 1 ? 'A totals row is' : `${totals.length} totals rows are`} left out: "${totals[0].text}" (${rowsOf(totals)}).`)
  if (!s.fields.some((f) => f === 'name' || f === 'first_name' || f === 'last_name')) notes.push('No column is set as Name, so the address is used as the name.')
  if (notes.length) people.append(el('ul', { class: 'import-notes' }, ...notes.map((n) => el('li', {}, n))))
  // One-cell rows that look like area names are asked about before Add, never
  // dropped on a guess (Codex review 28 Sep 2026).
  let areaCard: HTMLElement | null = null
  const areaNames = built.lone.slice(0, 6).map((a) => `"${a.text}"`).join(', ')
  if (built.lone.length) {
    areaCard = el('section', { class: `import-card${sectionsAnswer === null ? ' import-old' : ''}` })
    const asAreas = el('button', { type: 'button', class: sectionsAnswer === null ? 'btn-primary' : 'link-button' }, sectionsAnswer === 'customers' ? 'No, they are areas' : 'Areas')
    const asCustomers = el('button', { type: 'button', class: sectionsAnswer === null ? 'btn-secondary' : 'link-button' }, sectionsAnswer === 'areas' ? 'No, they are customers' : 'Customers')
    asAreas.addEventListener('click', () => { sectionsAnswer = 'areas'; sectionsOff = false; drawCheck(box) })
    asCustomers.addEventListener('click', () => { sectionsAnswer = 'customers'; sectionsOff = true; drawCheck(box) })
    if (sectionsAnswer === null) {
      areaCard.append(el('strong', {}, 'Are these areas or customers?'),
        el('p', {}, `These rows have only one thing in them: ${areaNames}${built.lone.length > 6 ? '…' : ''}. If they are area names, each becomes the area for the customers under it.`),
        el('div', { class: 'import-actions' }, asAreas, asCustomers))
    } else {
      areaCard.append(el('p', {}, sectionsAnswer === 'areas' ? `Used as areas: ${areaNames}.` : `Kept as customers: ${areaNames}.`), sectionsAnswer === 'areas' ? asCustomers : asAreas)
    }
  }

  // Past visits: what was found, and the owner's answers.
  let visitsCard: HTMLElement | null = null
  const leaveDatesOut = (): void => {
    fieldsBeforeOut = [...s.fields]
    newestWhenOut = built.visits.to
    s.fields = s.fields.map((f) => (f === 'visit_date' ? 'skip' : f))
    sheetAnswer = 'out'
    drawCheck(box)
  }
  if (hasVisitCols) {
    visitsCard = el('section', { class: 'import-card import-visits' })
    visitsCard.append(el('h3', {}, 'Last visits'))
    const v = built.visits
    if (oldSheet) {
      const q = el('div', { class: 'import-old' })
      q.append(
        el('strong', {}, 'Is this sheet up to date?'),
        el('p', {}, `The newest visit in it is ${shortDate(v.to)}, ${howLongAgo(v.to!, today)}. If you've cleaned since without writing it down, leave the dates out, or your customers will show as overdue.`),
      )
      const out = el('button', { type: 'button', class: sheetAnswer === null ? 'btn-primary' : 'link-button' }, sheetAnswer === null ? 'Leave the dates out' : 'Leave the dates out instead')
      out.addEventListener('click', leaveDatesOut)
      if (sheetAnswer === null) {
        const keep = el('button', { type: 'button', class: 'btn-secondary' }, "It's up to date, keep them")
        keep.addEventListener('click', () => { sheetAnswer = 'keep'; drawCheck(box) })
        q.append(el('div', { class: 'import-actions' }, out, keep))
      } else {
        q.append(el('p', { class: 'muted' }, 'Kept. Customers whose last visit was long ago will show as due.'), out)
      }
      visitsCard.append(q)
    }
    if (!askSheet) {
      const lines: string[] = []
      lines.push(withVisits === 0 ? 'No past visit dates were found in the date columns.' : `A last visit for ${withVisits} ${withVisits === 1 ? 'customer' : 'customers'}${v.from && v.to ? `, from ${shortDate(v.from)} to ${shortDate(v.to)}` : ''}. Their next visit comes due from it.`)
      if (attach.length) lines.push(`${attach.length} of them ${attach.length === 1 ? 'is' : 'are'} already on your round and will get theirs too: ${attach.slice(0, 8).map((a) => `${a.name} (${shortDate(a.date)})`).join(', ')}${attach.length > 8 ? '…' : ''}.`)
      if (unsure) lines.push(`${unsure} already on your round could not be matched for certain, so they get no last visit. Add it on their page if you need to.`)
      if (v.future) lines.push(`${v.future} ${v.future === 1 ? 'date is' : 'dates are'} after today, so not a past visit: left out.`)
      if (v.unreadable) lines.push(`${v.unreadable} ${v.unreadable === 1 ? 'date' : 'dates'} couldn't be read: left out.`)
      visitsCard.append(el('ul', { class: 'import-notes' }, ...lines.map((l) => el('li', {}, l))))
      // "Is 03/04 the 3rd of April?" (a US or Canadian round, Step 2 round 2): said plainly with one of the sheet's
      // own dates, and changeable unless the sheet's dates already say which.
      if (orderCells.length) {
        const example = orderCells.find((c) => { const [a, b] = c.split(/[/.-]/).map(Number); return a !== b }) ?? orderCells[0]!
        const readAs = (o: DateOrder): string => { const got = parseVisitDate(example, null, 'recent', today, undefined, o).date; return got ? shortDate(got) : '' }
        if (sheetOrder) {
          visitsCard.append(el('p', { class: 'import-year-note' }, `Dates are read ${sheetOrder === 'mdy' ? 'month first' : 'day first'}, as your sheet writes them: "${example}" is ${readAs(sheetOrder)}.`))
        } else if (readAs('mdy') !== readAs('dmy')) {
          const pick = el('select', { 'aria-label': 'Which way round dates are written' }) as HTMLSelectElement
          pick.append(el('option', { value: 'mdy' }, `${readAs('mdy')} (month first)`), el('option', { value: 'dmy' }, `${readAs('dmy')} (day first)`))
          pick.value = dateOrder
          pick.addEventListener('change', () => { dateOrderChosen = pick.value === 'dmy' ? 'dmy' : 'mdy'; drawCheck(box) })
          visitsCard.append(el('label', { class: 'import-year' }, `Dates like "${example}" are `, pick))
        }
      }
      if (v.yearless > 0) {
        const thisYear = Number(today.slice(0, 4))
        const pick = el('select', { 'aria-label': 'Which year dates with no year are from' }) as HTMLSelectElement
        pick.append(el('option', { value: '' }, 'choose…'))
        pick.append(el('option', { value: 'recent' }, 'the last 12 months'))
        for (const y of [thisYear, thisYear - 1, thisYear - 2]) pick.append(el('option', { value: String(y) }, String(y)))
        pick.value = yearChosen ? String(yearMode) : 'recent'
        pick.addEventListener('change', () => {
          if (!pick.value) return
          yearChosen = true
          yearMode = pick.value === 'recent' ? 'recent' : Number(pick.value)
          drawCheck(box)
        })
        visitsCard.append(el('label', { class: 'import-year' }, `${v.yearless} ${v.yearless === 1 ? 'date has' : 'dates have'} no year, like "16 Oct". They are from `, pick))
        // Said plainly when "the last 12 months" puts some of them in an earlier year, and then it needs a yes.
        if (yearRolledBack && !yearChosen && yearMode === 'recent') {
          const ok = el('button', { type: 'button', class: 'btn-secondary' }, 'Yes, the last 12 months')
          ok.addEventListener('click', () => { yearChosen = true; drawCheck(box) })
          visitsCard.append(el('p', { class: 'import-year-note' }, `So a date later in the year than today, like "16 Oct", is taken as last year. `, ok))
        }
      }
      if (withVisits > 0 && visitsCovered) {
        visitsCard.append(el('p', { class: 'muted' }, "Each one is in that customer's opening balance from Squeegee, so it isn't counted again in your takings."))
      } else if (withVisits > 0) {
        const q = el('fieldset', { class: 'import-paid' })
        q.append(el('legend', {}, 'Were these last visits paid?'))
        for (const [val, label] of [[true, 'Yes, they were all paid'], [false, "Not sure: show them under Not recorded on Owed"]] as const) {
          const r = el('input', { type: 'radio', name: 'import-visits-paid' }) as HTMLInputElement
          r.checked = visitsPaid === val
          r.addEventListener('change', () => { visitsPaid = val; drawCheck(box) })
          q.append(el('label', { class: 'import-paid-choice' }, r, ` ${label}`))
        }
        visitsCard.append(q)
      }
    }
  } else if (sheetAnswer === 'out') {
    visitsCard = el('section', { class: 'import-card import-visits' })
    const back = el('button', { type: 'button', class: 'link-button' }, 'Put the dates back')
    back.addEventListener('click', () => {
      const before = fieldsBeforeOut
      if (before) s.fields = s.fields.map((f, c) => (before[c] === 'visit_date' && f === 'skip' ? 'visit_date' : f))
      fieldsBeforeOut = null
      newestWhenOut = null
      sheetAnswer = 'keep'
      drawCheck(box)
    })
    visitsCard.append(
      el('h3', {}, 'Last visits'),
      el('p', {}, `Left out, because the newest visit in this sheet is ${newestWhenOut ? `${shortDate(newestWhenOut)}, ${howLongAgo(newestWhenOut, today)}` : 'old'}. Each customer comes due from the first clean you tick off in Roundly.`),
      back,
    )
  }

  // "Check these first" (Astra review 28 Sep 2026): where Roundly found the five
  // things that matter, each with its value for the first customer who has
  // one, so a believable but wrong price is seen before Add. Price, how often
  // and name can be changed here; address and phone in the column list.
  let keyBox: HTMLElement | null = null
  if (fresh.length) {
    keyBox = el('section', { class: 'import-card import-key' })
    keyBox.append(el('h3', {}, 'Check these first'), el('p', { class: 'muted' }, 'Where Roundly found the things that matter, with an example from your sheet.'))
    const sources = (want: ImportField[]): number[] => s.fields.map((f, c) => (want.includes(f) ? c : -1)).filter((c) => c >= 0)
    const firstWith = (get: (r: ImportRow) => string): { text: string; who: string } | null => {
      for (const p of fresh) { const t = get(p.row); if (t) return { text: t, who: p.row.name } }
      return null
    }
    const jump = (field: ImportField): HTMLElement => {
      const b = el('button', { type: 'button', class: 'link-button' }, 'Change')
      b.addEventListener('click', () => {
        const c = s.fields.indexOf(field)
        const target = list.children[c >= 0 ? c : 0] as HTMLElement | undefined
        // The column list is folded by default: open it first, or Change would do nothing (Codex, 29 Sep).
        const body = box.querySelector<HTMLElement>('#import-cols-body')
        if (body?.hidden) {
          colsOpen = true
          body.hidden = false
          box.querySelector('.import-cols-toggle')?.setAttribute('aria-expanded', 'true')
        }
        target?.scrollIntoView({ block: 'center' })
        target?.querySelector('select')?.focus()
      })
      return b
    }
    const chooser = (field: ImportField, label: string): HTMLSelectElement => {
      const pick = el('select', { 'aria-label': `Which column has the ${label.toLowerCase()}` }) as HTMLSelectElement
      pick.append(el('option', { value: '' }, 'None'))
      s.fields.forEach((_, c) => {
        const sample = s.body.map((r) => r[c]).find((v) => v?.trim())
        pick.append(el('option', { value: String(c) }, `${colName(c)}${sample ? `, e.g. ${sample.slice(0, 24)}` : ''}`))
      })
      const now = s.fields.indexOf(field)
      pick.value = now >= 0 ? String(now) : ''
      pick.addEventListener('change', () => {
        // The old column keeps its words in notes.
        s.fields = s.fields.map((f) => (f === field ? 'notes' : f))
        if (pick.value !== '') s.fields[Number(pick.value)] = field
        if (field === 'price') priceAnswered = true
        drawCheck(box)
      })
      return pick
    }
    const row = (label: string, from: number[], example: { text: string; who: string } | null, have: number, control: HTMLElement, warn?: string, where?: string): void => {
      const said = from.length ? `from ${from.map((c) => `"${colName(c)}"`).join(', ')}` : where ?? 'not found in your sheet'
      const none = fresh.length - have
      const item = el('div', { class: 'import-key-row' }, el('div', { class: 'import-key-words' }, el('strong', {}, label), el('span', {}, said),
        example ? el('span', { class: 'muted' }, label === 'Name' ? `e.g. ${example.text}` : `${example.text} for ${example.who}`) : '',
        none > 0 ? el('span', { class: 'import-missing' }, have === 0 ? `None of your ${fresh.length} customers has one` : `${none} of your ${fresh.length} customers ${none === 1 ? 'has' : 'have'} none`) : '',
        warn ? el('span', { class: 'import-missing' }, warn) : ''), control)
      keyBox!.append(item)
    }
    const count = (get: (r: ImportRow) => unknown): number => fresh.filter((p) => { const v = get(p.row); return v !== undefined && v !== null && v !== '' }).length
    const nameCols = sources(['name', 'first_name', 'last_name'])
    row('Name', nameCols, firstWith((r) => r.name), fresh.length, s.fields.includes('first_name') || s.fields.includes('last_name') ? jump('first_name') : chooser('name', 'Name'))
    // Name and address in one cell: the address comes from the name column.
    row('Address', sources(['address', 'postcode']), firstWith((r) => addressLine(r.address, r.postcode, ', ')), count((r) => r.address || r.postcode), jump('address'), undefined,
      nameCols.length ? `from ${nameCols.map((c) => `"${colName(c)}"`).join(', ')}, after the name` : undefined)
    row('Phone', sources(['phone']), firstWith((r) => r.phone ?? ''), count((r) => r.phone), jump('phone'))
    const unclear = fresh.filter((p) => p.priceWords)
    row('Regular price', sources(['price']), firstWith((r) => (r.reg_price_pence !== undefined ? money(r.reg_price_pence) : '')), count((r) => r.reg_price_pence), chooser('price', 'Regular price'),
      unclear.length ? `${unclear.length} unclear, like "${unclear[0].priceWords}": they come in without a price` : undefined)
    row('How often', sources(['cycle']), firstWith((r) => (r.cycle_days ? everyLabel(r.cycle_days) : r.cycle_text ?? '')), count((r) => r.cycle_days || r.cycle_text), chooser('cycle', 'How often'))
  }

  refreshGo()
  // Short enough to sit beside Add in the pinned bar on a phone.
  const other = el('button', { type: 'button', class: 'link-button' }, 'Use another file')
  other.addEventListener('click', () => showChoose(box))
  // The questions first, then what goes where (folded), then the customers, with Add at the end
  // (Liam, 29 Sep: "add customers is in the middle, that doesn't make sense").
  cols.querySelector('p')!.textContent = `From ${s.file}${s.sheetName ? `, sheet "${s.sheetName}"` : ''}. Only needed if a whole column is in the wrong place.`
  if (s.sheets.length > 1) {
    const pick = el('select', { 'aria-label': 'Which sheet has your customers' }) as HTMLSelectElement
    s.sheets.forEach((x, i) => pick.append(el('option', { value: String(i) }, x.name)))
    pick.value = String(s.sheets.findIndex((x) => x.name === s.sheetName))
    pick.addEventListener('change', () => useSheet(box, s.file, s.sheets, Number(pick.value)))
    cols.insertBefore(el('label', { class: 'import-sheet-pick import-sheet-choice' }, 'Sheet with your customers ', pick), cols.querySelector('.import-sheet-pick'))
  }
  // "Which column is the regular price?" when two headed price columns are as
  // likely as each other (Astra audit 28 Sep 2026: never chosen silently).
  let priceCard: HTMLElement | null = null
  if (tie.length) {
    priceCard = el('section', { class: 'import-card import-old' })
    const q = el('fieldset', { class: 'import-paid' })
    q.append(el('legend', {}, 'Which column is the regular price?'))
    const choose = (c: number | null): void => {
      s.fields = s.fields.map((f, i) => (f === 'price' || tie.includes(i) ? 'notes' : f))
      if (c !== null) s.fields[c] = 'price'
      priceAnswered = true
      drawCheck(box)
    }
    for (const c of tie) {
      const sample = s.body.map((r) => r[c]).find((v) => v?.trim())
      const r = el('input', { type: 'radio', name: 'import-price-tie' }) as HTMLInputElement
      r.addEventListener('change', () => choose(c))
      q.append(el('label', { class: 'import-paid-choice' }, r, ` "${colName(c)}"${sample ? `, e.g. ${sample}` : ''}`))
    }
    const none = el('input', { type: 'radio', name: 'import-price-tie' }) as HTMLInputElement
    none.addEventListener('change', () => choose(null))
    q.append(el('label', { class: 'import-paid-choice' }, none, ' None of these'))
    priceCard.append(el('p', {}, 'Your sheet has more than one price. The regular price is what a normal clean costs, not a first clean or extras.'), q)
  }
  // "Is 'Region' part of the address or the area?" (a repeating region that could be either).
  let regionCard: HTMLElement | null = null
  if (region >= 0) {
    regionCard = el('section', { class: 'import-card import-old' })
    const sample = s.body.map((r) => r[region]).find((v) => v?.trim()) ?? ''
    const asAddress = el('button', { type: 'button', class: 'btn-secondary' }, 'Part of the address')
    const asArea = el('button', { type: 'button', class: 'btn-secondary' }, 'The area of my round')
    asAddress.addEventListener('click', () => { regionAnswered = true; drawCheck(box) })
    // The owner's answer wins: any other area column goes to notes (Codex review).
    asArea.addEventListener('click', () => { s.fields = s.fields.map((f, c) => (c === region ? 'area' : f === 'area' ? 'notes' : f)); regionAnswered = true; drawCheck(box) })
    regionCard.append(el('strong', {}, `Is "${colName(region)}" part of the address or the area?`),
      el('p', {}, `Every customer has much the same one, like "${sample}".`), el('div', { class: 'import-actions' }, asAddress, asArea))
  }
  // "Which of these is the first name?" (no headings, the name in two columns).
  let nameCard: HTMLElement | null = null
  if (nameOrder.length) {
    nameCard = el('section', { class: 'import-card import-old' })
    const q = el('fieldset', { class: 'import-paid' })
    q.append(el('legend', {}, 'Which of these is the first name?'))
    for (const c of nameOrder) {
      const sample = s.body.map((r) => r[c]).find((v) => v?.trim()) ?? ''
      const r = el('input', { type: 'radio', name: 'import-name-order' }) as HTMLInputElement
      r.addEventListener('change', () => {
        const other = nameOrder.find((x) => x !== c)!
        s.fields[c] = 'first_name'
        s.fields[other] = 'last_name'
        drawCheck(box)
      })
      q.append(el('label', { class: 'import-paid-choice' }, r, ` ${colName(c)}, e.g. "${sample}"`))
    }
    nameCard.append(el('p', {}, "Your customers' names are in two columns."), q)
  }
  // "Is this right?": each name, address, area or price Roundly worked out from
  // a column's contents, with an example, a way to change it, and one button to
  // confirm them all. Never guessed silently (Liam, 29 Sep 2026).
  let guessCard: HTMLElement | null = null
  if (guessed.length) {
    guessCard = el('section', { class: 'import-card import-old' })
    guessCard.append(el('strong', {}, 'Is this right?'), el('p', {}, 'Roundly worked these out from what is in the columns, not from their headings.'))
    const WHAT: Partial<Record<ImportField, string>> = { name: 'the name', first_name: 'the first name', last_name: 'the surname', address: 'part of the address', area: 'the area', price: 'the regular price' }
    for (const c of guessed) {
      const sample = s.body.map((r) => r[c]).find((v) => v?.trim()) ?? ''
      const pick = el('select', { 'aria-label': `What "${colName(c)}" goes into` }) as HTMLSelectElement
      for (const [value, label] of Object.entries(FIELD_LABELS)) pick.append(el('option', { value }, postcodeWords(label)))
      pick.value = s.fields[c]
      pick.addEventListener('change', () => {
        const f = pick.value as ImportField
        // One name, area and price: the column it came from goes to notes.
        if (['name', 'area', 'price', 'postcode', 'cycle'].includes(f)) s.fields = s.fields.map((x, i) => (x === f && i !== c ? 'notes' : x))
        s.fields[c] = f
        drawCheck(box)
      })
      guessCard.append(el('div', { class: 'import-key-row' }, el('div', { class: 'import-key-words' },
        el('strong', {}, `"${colName(c)}" looks like ${postcodeWords(WHAT[s.fields[c]] ?? FIELD_LABELS[s.fields[c]].toLowerCase())}`),
        sample ? el('span', { class: 'muted' }, `e.g. "${sample.slice(0, 60)}"`) : ''), pick))
    }
    const ok = el('button', { type: 'button', class: 'btn-primary' }, guessed.length === 1 ? "Yes, that's right" : 'Yes, these are right')
    ok.addEventListener('click', () => { guessesConfirmedFor = JSON.stringify(s.fields); drawCheck(box) })
    guessCard.append(el('div', { class: 'import-actions' }, ok))
  }
  // "Check what goes where" folds to one bordered box above the customers: it is only needed when a
  // whole column is in the wrong place. It opens by itself while Roundly is unsure of a column (the
  // questions above), and stays as the owner left it. The sheet choice stays outside the fold. Opening
  // and closing does not redraw the screen, so nothing jumps.
  const unsureCols = s.headerUnsure || guessed.length > 0 || tie.length > 0 || region >= 0
  const colsShown = colsOpen || unsureCols
  cols.querySelector('h3')?.remove()
  const sheetChoice = cols.querySelector<HTMLElement>('.import-sheet-choice')
  sheetChoice?.remove()
  const colsBody = el('div', { class: 'import-cols-body import-card', id: 'import-cols-body' }, ...Array.from(cols.childNodes))
  cols.className = 'import-cols-card'
  colsBody.hidden = !colsShown
  const toggle = el('button', { type: 'button', class: 'import-cols-toggle', 'aria-expanded': String(colsShown), 'aria-controls': 'import-cols-body' },
    el('span', { class: 'import-cols-words' }, el('strong', {}, 'Check what goes where'), el('span', {}, 'Something in the wrong column? Change it here.')),
    el('span', { class: 'import-cols-chev', 'aria-hidden': 'true' }))
  toggle.addEventListener('click', () => {
    colsOpen = colsBody.hidden
    colsBody.hidden = !colsOpen
    toggle.setAttribute('aria-expanded', String(colsOpen))
  })
  cols.replaceChildren(toggle, ...(sheetChoice ? [sheetChoice] : []), colsBody)
  // Add stays in view at the foot of the screen while the list scrolls, and settles under the last customer.
  const goBar = el('div', { class: 'import-actions import-go-bar' }, go, other, why)
  // The "Changes sent" note sits just above the bar, however tall the bar grows (a waiting reason, a
  // wrapped button, the phone's home bar).
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(() => document.documentElement.style.setProperty('--import-bar-h', `${goBar.offsetHeight}px`)).observe(goBar)
  }
  const help = el('details', { class: 'import-review-help' }, el('summary', {}, 'Need a hand? Send us your file'), sendCard())
  box.append(summary, ...(headerCard ? [headerCard] : []), ...(areaCard ? [areaCard] : []), ...(priceCard ? [priceCard] : []), ...(regionCard ? [regionCard] : []), ...(nameCard ? [nameCard] : []), ...(guessCard ? [guessCard] : []), ...(keyBox ? [keyBox] : []), ...(visitsCard ? [visitsCard] : []), ...(moneyCard ? [moneyCard] : []), cols, people, help, goBar)
  if (openLine !== null) box.querySelector<HTMLInputElement>('.import-edit input')?.focus()
}

// One money question (PLAN section 4): who and what, in the owner's words, and its answers as buttons; a record that
// can't be read can also have its amount typed. Unanswered ones are marked until answered.
function moneyQuestion(q: MoneyQuestion, current: string | null, answer: (qid: string, value: string) => void): HTMLElement {
  const item = el('div', { class: `import-money-q${current === null ? ' is-open' : ''}` })
  item.append(el('p', {}, el('strong', {}, q.name || q.ref), ` ${q.says}`))
  const houses = q.kind === 'two_houses' ? q.says.split('? ')[1]?.split('; ').map((h) => h.replace(/^\d+: /, '')) ?? [] : []
  const row = el('div', { class: 'import-money-options', role: 'group', 'aria-label': `${q.name}: answer` })
  for (const o of q.options) {
    const b = el('button', { type: 'button', class: current === o ? 'btn-primary' : 'btn-secondary', 'aria-pressed': String(current === o) }, optionWords(q, o, houses))
    b.addEventListener('click', () => answer(q.id, o))
    row.append(b)
  }
  item.append(row)
  if (q.typed) {
    const typed = el('input', { type: 'text', inputmode: 'decimal', placeholder: 'Or type the amount, e.g. 12.50', 'aria-label': `${q.name}: the amount`, value: current?.startsWith('pence:') ? (Number(current.slice(6)) / 100).toFixed(2) : '' }) as HTMLInputElement
    typed.addEventListener('change', () => {
      try {
        const p = parseMoney(typed.value)
        if (p !== null) answer(q.id, `pence:${p}`)
      } catch {
        typed.setCustomValidity('Type the amount in pounds, for example 12.50')
        typed.reportValidity()
      }
    })
    item.append(el('label', { class: 'import-money-typed' }, typed))
  }
  return item
}

// What this import looked like and what the owner chose, for making the import
// better (Liam, 29 Sep 2026; told on the privacy page): the headings, Roundly's
// first choice for each column, the final choice, the questions and counts.
// Never a customer's details (the server keeps any heading that looks like one
// as "[detail]"), and never waited for: a failure changes nothing.
function sendFeedback(s: Sheet, counts: { rows: number; added: number; edited: number; unticked: number }): void {
  const kind = /\.(csv|tsv|txt)$/i.test(s.file) ? 'csv' : /\.(xlsx|xls|xlsm|ods|numbers)$/i.test(s.file) ? 'excel' : 'other'
  const body = {
    file_kind: kind,
    headings: (s.header ?? []).slice(0, 200).map((h) => (h ?? '').slice(0, 60)),
    guessed: s.firstFields.slice(0, 200),
    chosen: s.fields.slice(0, 200),
    questions: {
      header_row_guessed: s.headerGuessed,
      header_row_chosen: s.headerRow,
      header_unsure: s.headerUnsureAtStart,
      rows_above_kept: s.keepAbove.size,
      areas: sectionsAnswer ?? 'none',
      price_tie: priceAnswered,
      region: regionAnswered,
      guessed: s.guessedAtStart,
      guesses_confirmed: guessesConfirmedFor === JSON.stringify(s.fields),
      // Headings are kept only from a row Roundly was sure of or the owner confirmed.
      header_confirmed: s.headerRow >= 0 && !s.headerUnsure,
      sheet_old: sheetAnswer ?? 'none',
    },
    ...counts,
  }
  void fetch('/api/import-feedback', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'Idempotency-Key': crypto.randomUUID(), ...businessHeader() },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  }).catch(() => undefined)
}

function businessHeader(): Record<string, string> {
  const id = getMe()?.businessId
  return typeof id === 'number' ? { 'X-Roundly-Business': String(id) } : {}
}

type SendRow = Omit<ImportRow, 'last_visit' | 'next_visit'> & { last_visit?: { date: string; amount_pence?: number; paid: boolean }; next_visit?: { date: string; worker?: string; note: string }; brought?: Brought; status?: 'lapsed' }
type AttachVisit = { customer_id: number; date: string; amount_pence?: number; paid: boolean }

async function send(box: HTMLElement, rows: SendRow[], attach: AttachVisit[] = [], placed: Placed | null = null, resume: ReturnType<typeof resumeHistory> = []): Promise<void> {
  importStage('saving')
  clear(box)
  const bar = el('progress', { max: String(Math.max(1, rows.length + attach.length)), value: '0', class: 'import-progress' }) as HTMLProgressElement
  const status = el('p', { role: 'status' }, rows.length ? `Adding ${rows.length} customers…` : `Adding last visits to ${attach.length} customers…`)
  box.append(el('section', { class: 'import-card' }, status, bar))
  let added = 0
  let visitsAdded = 0
  let booked = 0
  let notAdded = 0
  const refused: string[] = []
  // Parts of at most 100 customers and 200 KB each.
  const parts: SendRow[][] = []
  let part: SendRow[] = []
  let size = 0
  for (const r of rows) {
    const bytes = JSON.stringify(r).length * 3
    if (part.length > 0 && (part.length >= CHUNK || size + bytes > CHUNK_BYTES)) {
      parts.push(part)
      part = []
      size = 0
    }
    part.push(r)
    size += bytes
  }
  if (part.length) parts.push(part)
  // Then the accounts of customers already here and a newer backup's figures, in parts of their own of at most 200
  // (release step 6; short check), and last, the last visits for customers already here, so each of those is "in
  // opening balance" once its account is on the round, whichever part brought it.
  type Payload = { rows: SendRow[]; attach: AttachVisit[]; accounts?: Placed['accounts']; corrections?: Placed['corrections']; expected?: Placed['expected']; money_on?: true }
  const payloads: Payload[] = parts.map((p) => ({ rows: p, attach: [] }))
  if (placed) {
    for (const p of partsOf(placed.accounts)) payloads.push({ rows: [], attach: [], accounts: p })
    for (const p of partsOf(placed.corrections)) payloads.push({ rows: [], attach: [], corrections: p })
  }
  for (let i = 0; i < attach.length; i += 200) payloads.push({ rows: [], attach: attach.slice(i, i + 200) })
  // The check screen's totals go with the first part, kept with the import for the server to be checked against.
  if (placed && payloads.length) payloads[0].expected = placed.expected
  // The owner accepted the money check: on a round whose money from Squeegee is still off, every part carrying money
  // says so, and the first one to arrive switches it on for this round (functions/api/customers/import.ts).
  if (placed && getMe()?.broughtMoney !== true) {
    for (const p of payloads) if (p.expected || p.accounts || p.corrections || p.rows.some((r) => r.brought)) p.money_on = true
  }
  let moneyAnswer: MoneyAnswer | null = null
  let done = 0
  // One batch for the whole import, so "Undo this import" takes back every part of it (0039).
  const batch = crypto.randomUUID()
  let batchId: number | null = null
  for (const payload of payloads) {
    const part = payload.rows
    // One key per part, kept for its retries: a part sent twice adds nobody twice.
    const key = crypto.randomUUID()
    type Answer = { created?: number; visits?: number; booked?: number; skipped?: { index: number; reason: string }[]; error?: string; batch_id?: number | null; brought?: MoneyAnswer }
    let answer: Answer | null = null
    for (let attempt = 0; attempt < 3 && !answer; attempt++) {
      try {
        const res = await fetch('/api/customers/import', {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'Idempotency-Key': key, ...businessHeader() },
          body: JSON.stringify(payload.attach.length || payload.accounts || payload.corrections || payload.expected || payload.money_on
            ? { ...payload, ...(payload.attach.length ? {} : { attach: undefined }), batch }
            : { rows: part, batch }),
          credentials: 'same-origin',
          signal: AbortSignal.timeout(30_000),
        })
        const json = (await res.json().catch(() => null)) as Answer | null
        if (res.ok) answer = json ?? { created: 0 }
        else if (res.status >= 400 && res.status < 500) answer = { created: 0, error: json?.error ?? 'refused' }
      } catch {
        // No answer at all: wait and send the same part again below.
      }
      if (!answer) await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)))
    }
    if (!answer) {
      // Who got added is now out of date: read it again next time.
      existingKeys = null
      status.textContent = `Added ${added} of ${rows.length}. The rest didn't send: check your signal, then import the same file again (anyone already added is left out).`
      toast('Import stopped part way. Nothing was added twice.')
      window.dispatchEvent(new CustomEvent('dropby:data-changed'))
      return
    }
    added += answer.created ?? 0
    visitsAdded += answer.visits ?? 0
    booked += answer.booked ?? 0
    if (typeof answer.batch_id === 'number') batchId = answer.batch_id
    // Each part answers the whole import's totals; figures accepted and overtaken ones add up across parts.
    if (answer.brought) {
      const before = moneyAnswer as MoneyAnswer | null
      moneyAnswer = { ...answer.brought, corrected: (before?.corrected ?? 0) + (answer.brought.corrected ?? 0), stale: [...(before?.stale ?? []), ...(answer.brought.stale ?? [])], switched_on: before?.switched_on === true || answer.brought.switched_on === true }
    }
    if (answer.error) {
      // The whole part was refused: every customer in it counts as not added.
      notAdded += part.length
      refused.push(part.length ? `${part.length} customers from ${part[0].name} to ${part[part.length - 1].name}: ${answer.error}` : `Last visits for customers already here: ${answer.error}`)
    }
    for (const s of answer.skipped ?? []) {
      notAdded++
      refused.push(`${part[s.index]?.name ?? 'A customer'}: ${s.reason}`)
    }
    done += part.length + payload.attach.length
    bar.value = done
    status.textContent = rows.length ? `Adding customers… ${Math.min(done, rows.length)} of ${rows.length}` : `Adding last visits… ${done} of ${attach.length}`
  }
  // The history brought with each account, once the customers are in (release step 6), in chunks of at most 500
  // lines and 200 KB; and any history still to arrive for accounts an earlier import of this file brought, under that
  // import (Astra, steps 5-6 finding 4). Each line is Squeegee's own, once per account, so a chunk sent again adds
  // nothing twice. A chunk that can't be sent stops here with "Try again", never the done screen (finding 5).
  const jobs: { batch: string; accounts: { squeegee_ref: string; lines: Placed['history'][number]['lines'] }[] }[] = []
  if (placed && placed.history.length) for (const c of historyChunks(placed.history)) jobs.push({ batch, accounts: c })
  for (const key of [...new Set(resume.map((r) => r.batch_key))]) {
    for (const c of historyChunks(resume.filter((r) => r.batch_key === key).map((r) => ({ squeegee_ref: r.squeegee_ref, lines: r.lines })))) jobs.push({ batch: key, accounts: c })
  }
  const incompleteBy = new Map<string, number>()
  const finish = (): void => {
    existingKeys = null
    window.dispatchEvent(new CustomEvent('dropby:data-changed'))
    const resumedIncomplete = [...incompleteBy.entries()].filter(([k]) => k !== batch).reduce((t, [, n]) => t + n, 0)
    // Switched on just now: the app learns it, so every change from here on says it records cash amounts.
    if (moneyAnswer?.switched_on) void loadMe()
    showDone(box, added, notAdded, refused, visitsAdded, batchId, booked, placed || resume.length ? { answer: moneyAnswer, placed, resumedIncomplete } : null)
  }
  const sendFrom = async (from: number): Promise<void> => {
    for (let i = from; i < jobs.length; i++) {
      status.textContent = 'Adding what Squeegee knew about their money…'
      let sent = false
      for (let attempt = 0; attempt < 3 && !sent; attempt++) {
        try {
          const res = await fetch('/api/customers/import/history', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'Idempotency-Key': crypto.randomUUID(), ...businessHeader() },
            body: JSON.stringify({ batch: jobs[i].batch, accounts: jobs[i].accounts }),
            credentials: 'same-origin',
            signal: AbortSignal.timeout(30_000),
          })
          const json = (await res.json().catch(() => null)) as { totals?: NonNullable<MoneyAnswer['totals']> } | null
          if (res.ok) {
            sent = true
            if (json?.totals) {
              incompleteBy.set(jobs[i].batch, json.totals.incomplete)
              if (jobs[i].batch === batch && moneyAnswer) moneyAnswer = { ...moneyAnswer, totals: json.totals }
            }
            break
          }
          if (res.status >= 400 && res.status < 500) break
        } catch {
          // No answer: send the same chunk again.
        }
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)))
      }
      if (!sent) {
        // Stopped part way: what came in stays, the rest waits for "Try again" (or the same file imported again).
        clear(box)
        const again = el('button', { type: 'button', class: 'btn-primary' }, 'Try again')
        again.addEventListener('click', () => {
          clear(box)
          box.append(el('section', { class: 'import-card' }, status, bar))
          void sendFrom(i)
        })
        box.append(el('section', { class: 'import-card' },
          el('h3', {}, 'Nearly done: their history from Squeegee is still to come'),
          el('p', {}, `${added ? `${added} ${added === 1 ? 'customer is' : 'customers are'} in, with their money. ` : ''}What Squeegee knew about their money didn't all send. Check your signal and try again, or import the same file again later: nothing is added twice.`),
          el('div', { class: 'import-actions' }, again)))
        return
      }
    }
    finish()
  }
  await sendFrom(0)
}


// What the server counted for the import's money after its last part (functions/api/customers/import.ts).
interface MoneyAnswer {
  accounts: number
  corrected: number
  totals: { accounts: number; owed_pence: number; owed_customers: number; credit_pence: number; credit_customers: number; incomplete: number } | null
  expected: Placed['expected'] | null
  // A newer backup's figures not taken because the account moved since the check (Astra, steps 5-6 finding 3).
  stale?: string[]
  // This import switched money from Squeegee on for the round (the owner accepted the money check).
  switched_on?: boolean
}
type MoneyDone = { answer: MoneyAnswer | null; placed: Placed | null; resumedIncomplete?: number }

// The done card's money lines (PLAN section 4): what came in, owed and credit apart, and any difference between what
// the server counted and what the check screen said, named.
function moneyDoneWords(m: MoneyDone): { lines: string[]; problems: string[] } {
  const lines: string[] = []
  const problems: string[] = []
  const n = (k: number): string => `${k} ${k === 1 ? 'customer' : 'customers'}`
  // History finished for accounts an earlier import brought: complete only when the server says so (finding 4).
  if ((m.resumedIncomplete ?? 0) > 0) problems.push(`The history of ${n(m.resumedIncomplete!)} brought before is still arriving: import the same file again to finish it.`)
  if (m.answer?.stale?.length) problems.push(`Not taken, because their money changed since you checked: ${m.answer.stale.slice(0, 6).join(', ')}. Import the same file again to check them.`)
  if (m.answer?.switched_on) lines.push('Money from Squeegee is now on for your round.')
  const t = m.answer?.totals
  if (!m.placed) return { lines, problems }
  const e = m.placed.expected
  if (!t) {
    if (e.accounts) problems.push("Roundly couldn't check the money that came in. Open Owed to see it.")
    return { lines, problems }
  }
  if (t.owed_pence) lines.push(`${pounds(t.owed_pence)} owed by ${n(t.owed_customers)} came in from Squeegee, on Owed now.`)
  if (t.credit_pence) lines.push(`${pounds(t.credit_pence)} credit for ${n(t.credit_customers)}, set aside for their next cleans.`)
  if ((m.answer?.corrected ?? 0) > 0) lines.push(`${m.answer!.corrected} ${m.answer!.corrected === 1 ? 'figure' : 'figures'} from a newer backup accepted.`)
  if (t.owed_pence === e.owed_pence && t.credit_pence === e.credit_pence && t.accounts === e.accounts) {
    if (e.accounts) lines.push('Checked: the same as the check screen said.')
  } else {
    problems.push(`The check screen said ${pounds(e.owed_pence)} owed and ${pounds(e.credit_pence)} credit for ${n(e.accounts)}; Roundly counted ${pounds(t.owed_pence)} owed and ${pounds(t.credit_pence)} credit for ${n(t.accounts)}. Check Owed, or import the same file again (nobody is added twice).`)
  }
  if (t.incomplete > 0) problems.push(`The history of ${n(t.incomplete)} is still arriving: import the same file again to finish it. Their money already counts.`)
  if (m.placed.notPlaced.length) problems.push(`Not brought, because the customer isn't coming in: ${m.placed.notPlaced.slice(0, 6).map((x) => `${x.name} (${x.pence > 0 ? `${pounds(x.pence)} owed` : `${pounds(x.pence)} credit`})`).join(', ')}${m.placed.notPlaced.length > 6 ? '…' : ''}.`)
  return { lines, problems }
}

function showDone(box: HTMLElement, added: number, notAdded: number, refused: string[], visitsAdded = 0, batchId: number | null = null, booked = 0, money: MoneyDone | null = null): void {
  importStage('result')
  clear(box)
  const card = el('section', { class: 'import-card import-done' })
  const heading = el('h3', {}, added === 0 && visitsAdded === 0 && money ? 'Money brought in from Squeegee' : added === 0 && visitsAdded > 0 ? `Last visits added for ${visitsAdded} ${visitsAdded === 1 ? 'customer' : 'customers'}` : added === 1 ? '1 customer added to your round' : `${added} customers added to your round`)
  // A green tick when something came in; an import that added nothing gets none.
  card.append(added > 0 || visitsAdded > 0 || (money?.answer?.accounts ?? 0) + (money?.answer?.corrected ?? 0) > 0 ? el('div', { class: 'import-done-head' }, el('span', { class: 'import-done-tick', 'aria-hidden': 'true' }, '✓'), heading) : heading)
  // The number counts up to what was added (does nothing with reduce motion).
  if (added > 1) countBetween(heading, (k) => `${Math.round(added * k)} customers added to your round`, Math.min(1400, 500 + added * 4))
  if (visitsAdded) card.append(el('p', {}, added === 0 ? 'Who is due now shows on your diary and in Messages.' : `${visitsAdded} last ${visitsAdded === 1 ? 'visit' : 'visits'} added, so who is due shows on your diary and in Messages.`))
  if (booked) card.append(el('p', {}, `${booked} next ${booked === 1 ? 'visit' : 'visits'} booked in the diary, on the dates your old app had.`))
  if (money) {
    const words = moneyDoneWords(money)
    card.append(...words.lines.map((l) => el('p', {}, l)), ...words.problems.map((l) => el('p', { class: 'import-money-problem' }, l)))
  }
  if (notAdded) card.append(el('p', { class: 'muted' }, `${notAdded} not added:`), el('ul', { class: 'import-notes' }, ...refused.slice(0, 10).map((r) => el('li', {}, r))))
  // Everyone came in with their next visit booked (Squeegee's Next Due): nothing left to book, and the line
  // above already says so.
  const allBooked = booked > 0 && booked >= added
  const nextWords = 'Next, book their next visits in the diary.'
  const next = el('p', allBooked ? { hidden: '' } : {}, nextWords)
  const diary = el('a', { href: '#/schedule', class: 'btn-primary import-next' }, 'Open the diary')
  // A real button, not an underlined link (Liam, 29 Sep: "that hyperlink looks basic").
  const work = el('a', { href: '#/grid', class: 'btn-secondary import-next' }, 'See them in Work')
  const actions = el('div', { class: 'import-actions' }, diary, work)
  card.append(next, actions)
  box.append(card)
  // "Undo this import", under the result, for 7 days (0039): what it would remove is read from the
  // server, so it is shown only once it is known.
  if (batchId !== null && (added > 0 || visitsAdded > 0 || (money?.answer?.accounts ?? 0) > 0)) {
    void undoableBatches().then((page) => {
      const b = page.batches.find((x) => x.id === batchId)
      if (b && card.isConnected) card.append(undoCard(b, true))
    })
  }
  // Nothing came in: no getting-started steps (their "Import a list" would point at this same page).
  if (added === 0 && visitsAdded === 0) return
  const steps = el('div', { class: 'welcome-slot import-welcome' })
  box.append(steps)
  // Customers who came in with no dates are on no list until they get one (Liam, 29 Sep: "the rest could
  // easily get lost"). Then the one thing to do next is give them dates: the button opens the diary's
  // "No date yet" line, which glows once when the diary opens. Otherwise a new round sees the diary's own
  // three getting-started steps here (Liam, 29 Sep: this page is part of onboarding), which carry the
  // next action while they show, so "Open the diary" and its line go; a round that finished or hid them
  // keeps both.
  const draw = (): void => {
    void api<{ customers: RoundCustomer[] }>('/api/customers', 'customers').then((r) => summariseRound(r.customers).nodate.length).catch(() => null).then((waiting) => {
      if (!card.isConnected) return
      if (waiting === null) {
        steps.replaceChildren()
        next.hidden = false
        next.textContent = 'Your import finished. Roundly could not check which customers need dates just now.'
        if (!next.isConnected) card.insertBefore(next, actions)
        const retry = el('button', { type: 'button', class: 'btn-primary import-next' }, 'Check next steps again')
        retry.addEventListener('click', () => { retry.disabled = true; draw() })
        actions.replaceChildren(retry, work)
        return
      }
      if (waiting > 0) {
        markNoDateForGlow()
        steps.replaceChildren()
        next.hidden = false
        next.textContent = `${waiting === 1 ? '1 customer has' : `${waiting} customers have`} no date yet, so nothing will remind you about them. Give each one a date so they don't get missed.`
        const give = el('button', { type: 'button', class: 'btn-primary import-next' }, 'Give them dates')
        give.addEventListener('click', () => goToNoDate())
        if (!next.isConnected) card.insertBefore(next, actions)
        actions.replaceChildren(give, work)
        return
      }
      next.hidden = allBooked
      next.textContent = nextWords
      void renderWelcome(steps).then(() => {
        if (steps.childElementCount) {
          next.remove()
          actions.replaceChildren(work)
        } else {
          if (!next.isConnected) card.insertBefore(next, actions)
          actions.replaceChildren(diary, work)
        }
      })
    })
  }
  draw()
  // The app does not redraw the import screen when something is saved, so a visit booked from these
  // steps would leave them stale: redraw them here while this page is showing.
  const onChange = (): void => {
    if (!steps.isConnected) {
      window.removeEventListener('dropby:data-changed', onChange)
      return
    }
    draw()
  }
  window.addEventListener('dropby:data-changed', onChange)
}
